import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  Tabs,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import ImportZipModulariseReviewDialog from "./ImportZipModulariseReviewDialog";
import { runImportZipModulariseReview } from "../utils/importZipReviewApi";
import {
  buildImportReviewCoverage,
  getImportReviewCoverageHint,
} from "../utils/importZipReviewCoverage";
import {
  buildAgentHandoffSummary,
  buildDiffReviewPrompt,
} from "../utils/devIdePromptGenerators";

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkdownToHtml(text = "") {
  let html = escapeHtml(text);
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|\W)\*([^*]+)\*(?=\W|$)/g, "$1<em>$2</em>");
  html = html.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, "$1<em>$2</em>");
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer">$1</a>',
  );
  return html;
}

function MarkdownPreview({ content }) {
  const lines = (content || "").replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) {
        i += 1;
      }
      nodes.push(
        <Box
          key={`md-node-${key++}`}
          component="pre"
          sx={{
            backgroundColor: "grey.100",
            borderRadius: 1,
            p: 1.5,
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 13,
            m: 0,
            mb: 1.5,
          }}
        >
          {codeLines.join("\n")}
        </Box>,
      );
      continue;
    }

    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
      nodes.push(<Divider key={`md-node-${key++}`} sx={{ my: 2 }} />);
      i += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const variantMap = {
        1: "h5",
        2: "h6",
        3: "subtitle1",
        4: "subtitle2",
        5: "body1",
        6: "body2",
      };
      nodes.push(
        <Typography
          key={`md-node-${key++}`}
          variant={variantMap[level] || "body1"}
          sx={{ mt: level <= 2 ? 2 : 1.5, mb: 1, fontWeight: 700 }}
          dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(text) }}
        />,
      );
      i += 1;
      continue;
    }

    if (/^>\s+/.test(trimmed)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s+/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box
          key={`md-node-${key++}`}
          sx={{
            borderLeft: 3,
            borderColor: "divider",
            pl: 1.5,
            py: 0.5,
            mb: 1.5,
          }}
        >
          {quoteLines.map((quoteLine, index) => (
            <Typography
              key={`quote-${index}`}
              variant="body2"
              color="text.secondary"
              dangerouslySetInnerHTML={{
                __html: inlineMarkdownToHtml(quoteLine),
              }}
            />
          ))}
        </Box>,
      );
      continue;
    }

    if (/^(-|\*)\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*(-|\*)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*(-|\*)\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box
          key={`md-node-${key++}`}
          component="ul"
          sx={{ mt: 0.5, mb: 1.5, pl: 3 }}
        >
          {items.map((item, index) => (
            <li key={`li-${index}`}>
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(item) }}
              />
            </li>
          ))}
        </Box>,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      nodes.push(
        <Box
          key={`md-node-${key++}`}
          component="ol"
          sx={{ mt: 0.5, mb: 1.5, pl: 3 }}
        >
          {items.map((item, index) => (
            <li key={`ol-${index}`}>
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(item) }}
              />
            </li>
          ))}
        </Box>,
      );
      continue;
    }

    const paragraphLines = [trimmed];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("#") &&
      !lines[i].trim().startsWith(">") &&
      !lines[i].trim().startsWith("```") &&
      !/^\s*(-|\*)\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^---+$/.test(lines[i].trim()) &&
      !/^\*\*\*+$/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim());
      i += 1;
    }

    nodes.push(
      <Typography
        key={`md-node-${key++}`}
        variant="body2"
        sx={{ mb: 1.5, whiteSpace: "normal", lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{
          __html: inlineMarkdownToHtml(paragraphLines.join(" ")),
        }}
      />,
    );
  }

  if (!nodes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing to preview yet.
      </Typography>
    );
  }

  return <>{nodes}</>;
}

function TrackedDocumentDiff({ diff }) {
  if (!diff?.has_changes) {
    return (
      <Typography variant="body2" color="text.secondary">
        No content changes detected.
      </Typography>
    );
  }

  const visibleLines = (diff.lines || []).filter(
    (line) => line.type !== "meta",
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {visibleLines.map((line, index) => {
        const isAdded = line.type === "added";
        const isRemoved = line.type === "removed";
        const cleanText = (line.text || "").replace(/^[+-]/, "");

        return (
          <Box
            key={`tracked-${index}`}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              border: 1,
              borderColor: isAdded
                ? "success.light"
                : isRemoved
                  ? "error.light"
                  : "divider",
              backgroundColor: isAdded
                ? "success.50"
                : isRemoved
                  ? "error.50"
                  : "background.paper",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Chip
                size="small"
                label={
                  isAdded ? "Inserted" : isRemoved ? "Deleted" : "Unchanged"
                }
                color={isAdded ? "success" : isRemoved ? "error" : "default"}
                variant={isAdded || isRemoved ? "filled" : "outlined"}
                sx={{ mt: 0.25 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily:
                    cleanText.trim().startsWith("#") ||
                    cleanText.trim().startsWith("-") ||
                    cleanText.trim().startsWith("*")
                      ? "monospace"
                      : "inherit",
                  textDecoration: isRemoved ? "line-through" : "none",
                  fontWeight: isAdded ? 600 : 400,
                }}
                dangerouslySetInnerHTML={{
                  __html: inlineMarkdownToHtml(cleanText || " "),
                }}
              />
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function isBinaryImportItem(file) {
  return Boolean(file && file.is_binary);
}

function getBinaryImportStatus(file = {}) {
  return String(file.status || file.change_type || "changed").toLowerCase();
}

function getCodeImportChangeKind(file) {
  if (!file) return "unknown";
  if (isBinaryImportItem(file)) {
    const binaryStatus = getBinaryImportStatus(file);
    if (binaryStatus === "unchanged") return "unchanged";
    if (binaryStatus === "new" || !file.file_id) return "new";
    return "changed";
  }
  if (!file.file_id) return "new";
  if ((file.original_content || "") === (file.updated_content || ""))
    return "unchanged";
  return "changed";
}

function getDocumentImportChangeKind(doc) {
  if (!doc) return "unknown";
  if (!doc.exists) return "new";
  if ((doc.original_content || "") === (doc.updated_content || ""))
    return "unchanged";
  return "changed";
}

function getSqlArtifactPath(artifact = {}) {
  return (
    artifact.sql_file_path ||
    artifact.file_path ||
    artifact.path ||
    artifact.sql_name ||
    artifact.name ||
    "Imported SQL artifact"
  );
}

function getSqlArtifactPreviewText(artifact = {}) {
  return (
    artifact.sql_content ||
    artifact.content ||
    artifact.safe_sql_preview ||
    artifact.sql_preview ||
    artifact.preview ||
    ""
  );
}

function truncateSqlPreview(value = "", maxLength = 1200) {
  const text = value || "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}\n... SQL preview truncated in Import Review ...`;
}

const EXPECTED_IMPORT_PATH_PREFIXES = [
  "backend/gui_viewer_api/modules/",
  "src/components/modules/",
  "docs/modules/",
];

function isExpectedProjectModulePath(path = "") {
  return EXPECTED_IMPORT_PATH_PREFIXES.some((prefix) =>
    path.startsWith(prefix),
  );
}

function getImportReviewPath(item = {}) {
  return item.path || item.file_path || item.slug || "";
}

function getRiskGateSeverity(riskGate) {
  if (!riskGate) return "info";
  if (riskGate.decision === "block_automation" || riskGate.risk_level === "high") return "error";
  if (riskGate.decision === "require_human_review" || riskGate.risk_level === "medium") return "warning";
  return "success";
}

function getRiskGateDecisionLabel(riskGate) {
  if (!riskGate) return "Risk gate not run";
  if (riskGate.decision === "allow_save") return "Save automation allowed";
  if (riskGate.decision === "require_human_review") return "Human review required before Save All";
  if (riskGate.decision === "block_automation") return "Save automation blocked";
  return riskGate.decision || "Risk gate result unknown";
}

function toReviewStatusLabel(status) {
  if (status === "saving") return "Saving";
  if (status === "saved") return "Saved";
  if (status === "error") return "Error";
  return "Ready";
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function shortHash(value = "") {
  const text = String(value || "");
  if (!text) return "no hash";
  return text.length > 12 ? `${text.slice(0, 12)}…` : text;
}

function getBinaryExtension(file = {}) {
  const ext = file.extension || (file.path || "").match(/\.[^./]+$/)?.[0] || "binary";
  return String(ext || "binary").replace(/^\./, "").toUpperCase();
}

function getBinaryReviewLabel(file = {}) {
  return file.label || (file.status === "unsupported" ? "Unsupported binary" : "Binary asset");
}

function canSaveImportItem(file = {}) {
  if (!isBinaryImportItem(file)) return true;
  const status = getBinaryImportStatus(file);
  return !["unsupported", "rejected", "blocked"].includes(status);
}

function BinaryAssetDetails({ file }) {
  if (!isBinaryImportItem(file)) return null;
  const status = getBinaryImportStatus(file);
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={getBinaryReviewLabel(file)} color={canSaveImportItem(file) ? "info" : "error"} variant="outlined" />
      <Chip size="small" label={`Type: ${getBinaryExtension(file)}`} variant="outlined" />
      <Chip size="small" label={`Size: ${formatBytes(file.size_bytes)}`} variant="outlined" />
      <Chip size="small" label={`SHA: ${shortHash(file.sha256)}`} variant="outlined" />
      {file.original_sha256 ? (
        <Chip size="small" label={`Old SHA: ${shortHash(file.original_sha256)}`} variant="outlined" />
      ) : null}
      <Chip
        size="small"
        label={`Binary status: ${status || "changed"}`}
        color={canSaveImportItem(file) ? "default" : "error"}
        variant={canSaveImportItem(file) ? "outlined" : "filled"}
      />
    </Stack>
  );
}

function buildGroupedImportReview({
  files = [],
  documents = [],
  skipped = [],
  skippedDocuments = [],
}) {
  const groups = new Map();

  const ensureGroup = (key, values) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: values.label,
        module: values.module,
        fileType: values.fileType,
        items: [],
        changedCount: 0,
        newCount: 0,
        unchangedCount: 0,
        skippedCount: 0,
      });
    }
    return groups.get(key);
  };

  (files || []).forEach((file) => {
    const moduleName = file.module || "unassigned";
    const kind = getCodeImportChangeKind(file);
    const key = `${moduleName}::code`;
    const group = ensureGroup(key, {
      label: `${moduleName} · Code`,
      module: moduleName,
      fileType: "code",
    });
    group.items.push({ ...file, assetType: "code", changeKind: kind });
    if (kind === "new") group.newCount += 1;
    else if (kind === "changed") group.changedCount += 1;
    else group.unchangedCount += 1;
  });

  (documents || []).forEach((doc) => {
    const moduleNames =
      doc.module_names && doc.module_names.length
        ? doc.module_names
        : ["shared docs"];
    const kind = getDocumentImportChangeKind(doc);
    moduleNames.forEach((moduleName) => {
      const key = `${moduleName}::docs`;
      const group = ensureGroup(key, {
        label: `${moduleName} · Documents`,
        module: moduleName,
        fileType: "docs",
      });
      group.items.push({ ...doc, assetType: "docs", changeKind: kind });
      if (kind === "new") group.newCount += 1;
      else if (kind === "changed") group.changedCount += 1;
      else group.unchangedCount += 1;
    });
  });

  (skipped || []).forEach((item) => {
    const moduleName = item.module || "unassigned";
    const key = `${moduleName}::code`;
    const group = ensureGroup(key, {
      label: `${moduleName} · Code`,
      module: moduleName,
      fileType: "code",
    });
    group.skippedCount += 1;
  });

  (skippedDocuments || []).forEach((item) => {
    const key = `shared docs::docs`;
    const group = ensureGroup(key, {
      label: `shared docs · Documents`,
      module: "shared docs",
      fileType: "docs",
    });
    group.skippedCount += 1;
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.module === b.module) return a.fileType.localeCompare(b.fileType);
    return a.module.localeCompare(b.module);
  });
}

function renderStatusChipProps(status) {
  if (status === "saved") return { color: "success", variant: "filled" };
  if (status === "error") return { color: "error", variant: "filled" };
  if (status === "saving") return { color: "warning", variant: "filled" };
  return { color: "default", variant: "outlined" };
}

const POST_IMPORT_ACTIONS = {
  frontendDependencyInstall: {
    label: "Run Frontend Dependency Install",
    endpoint: "/api/dev-ide/frontend-dependency-install",
    stepKey: "frontendDependencyInstallCompleted",
    runningMessage: "Frontend dependency install/check started from Import Review modal.",
    successMessage: "Frontend dependency install/check completed.",
    skippedMessage: "Frontend dependency install/check skipped because no command is configured.",
    failurePrefix: "Frontend dependency install/check failed",
    unconfiguredMessages: ["no frontend dependency install/check command configured"],
  },
  backendDependencyCheck: {
    label: "Run Backend Dependency Check",
    endpoint: "/api/dev-ide/backend-dependency-check",
    stepKey: "backendDependencyCheckCompleted",
    runningMessage: "Backend dependency check started from Import Review modal.",
    successMessage: "Backend dependency check completed.",
    skippedMessage: "Backend dependency check skipped because no command is configured.",
    failurePrefix: "Backend dependency check failed",
    unconfiguredMessages: ["no backend dependency check command configured"],
  },
  frontendBuild: {
    label: "Run Frontend Build",
    endpoint: "/api/dev-ide/rebuild-sandbox-frontend",
    stepKey: "frontendBuildCompleted",
    runningMessage: "Frontend build started from Import Review modal.",
    successMessage: "Frontend build completed.",
    skippedMessage: "Frontend build skipped because no command is configured.",
    failurePrefix: "Frontend build failed",
    unconfiguredMessages: ["no frontend rebuild command configured"],
  },
  backendRestart: {
    label: "Restart Backend",
    endpoint: "/api/dev-ide/restart-sandbox-backend",
    stepKey: "backendRestartCompleted",
    runningMessage: "Backend restart started from Import Review modal.",
    successMessage: "Backend restart completed.",
    skippedMessage: "Backend restart skipped because no command is configured.",
    failurePrefix: "Backend restart failed",
    unconfiguredMessages: ["no backend restart command configured"],
  },
  backendHealth: {
    label: "Run Backend Health Check",
    endpoint: "/api/dev-ide/backend-health-check",
    stepKey: "backendHealthCheckCompleted",
    runningMessage: "Backend health check started from Import Review modal.",
    successMessage: "Backend health check completed.",
    skippedMessage: "Backend health check skipped because no API base URL is configured.",
    failurePrefix: "Backend health check failed",
    unconfiguredMessages: ["no api_base_url configured"],
  },
};

function createInitialPostImportStatus() {
  return Object.keys(POST_IMPORT_ACTIONS).reduce((acc, key) => {
    acc[key] = { status: "Not run", output: "", error: "" };
    return acc;
  }, {});
}

function renderVerificationChipProps(status) {
  if (status === "Passed") return { color: "success", variant: "filled" };
  if (status === "Failed") return { color: "error", variant: "filled" };
  if (status === "Running") return { color: "warning", variant: "filled" };
  if (status === "Skipped") return { color: "info", variant: "outlined" };
  return { color: "default", variant: "outlined" };
}

function summariseCommandResult(data = {}) {
  const parts = [];
  if (typeof data.exit_code === "number") parts.push(`Exit code: ${data.exit_code}`);
  if (typeof data.status_code === "number") parts.push(`HTTP status: ${data.status_code}`);
  if (data.debug_project_cmd?.source) parts.push(`Command source: ${data.debug_project_cmd.source}`);
  if (data.debug_project?.health_url) parts.push(`Health URL: ${data.debug_project.health_url}`);

  const outputText = String(data.output || "").trim();
  const errorText = String(data.error || data.error_output || "").trim();
  if (outputText) parts.push(`Output:\n${outputText.slice(-2500)}`);
  if (errorText) parts.push(`Error:\n${errorText.slice(-2500)}`);

  return parts.length ? parts.join("\n\n") : "No output returned.";
}

function MobileReviewCardList({
  items,
  type,
  onPreviewDiff,
  onTrackedPreview,
  onSave,
  loading,
}) {
  if (!items.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No items in this view.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.25}>
      {items.map((item) => {
        const isDoc = type === "docs";
        const status = item._status || "ready";
        const statusChipProps = renderStatusChipProps(status);
        const changeKind = isDoc
          ? getDocumentImportChangeKind(item)
          : getCodeImportChangeKind(item);
        return (
          <Card key={item.path} variant="outlined">
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  sx={{ wordBreak: "break-word" }}
                >
                  {item.path}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={isDoc ? "Document" : isBinaryImportItem(item) ? "Binary asset" : "Code"}
                  />
                  <Chip
                    size="small"
                    label={
                      changeKind === "new"
                        ? "New"
                        : changeKind === "changed"
                          ? "Changed"
                          : "Unchanged"
                    }
                    color={
                      changeKind === "new"
                        ? "info"
                        : changeKind === "changed"
                          ? "warning"
                          : "default"
                    }
                    variant={changeKind === "unchanged" ? "outlined" : "filled"}
                  />
                  <Chip
                    size="small"
                    label={toReviewStatusLabel(status)}
                    color={statusChipProps.color}
                    variant={statusChipProps.variant}
                  />
                </Stack>
                {isDoc ? (
                  <Typography variant="caption" color="text.secondary">
                    Modules: {(item.module_names || []).join(", ") || "None"}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Module: {item.module || "None"}
                  </Typography>
                )}
                <BinaryAssetDetails file={item} />
                {item._message ? (
                  <Typography
                    variant="caption"
                    color={status === "error" ? "error" : "text.secondary"}
                  >
                    {item._message}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {!isBinaryImportItem(item) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onPreviewDiff(item)}
                      disabled={loading}
                    >
                      {isDoc ? "Raw diff" : "Preview diff"}
                    </Button>
                  ) : null}
                  {isDoc ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onTrackedPreview(item)}
                      disabled={loading}
                    >
                      Tracked preview
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onSave(item)}
                    disabled={
                      status === "saved" ||
                      status === "saving" ||
                      loading ||
                      !canSaveImportItem(item)
                    }
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

// Match the existing 25 MiB limit of /api/dev-ide/import-zip-from-path.
// These checks are only preliminary; the server validates ZIP contents and paths.
const MAX_PICKER_ZIP_BYTES = 25 * 1024 * 1024;

function validatePickedZip(file) {
  if (!file) return "Choose a ZIP file first.";
  if (!/\.zip$/i.test(String(file.name || ""))) return "Select a .zip file (not a PDF, text file, or other format).";
  if (!Number.isFinite(file.size) || file.size <= 0) return "The selected ZIP is empty or has an invalid file size.";
  if (file.size > MAX_PICKER_ZIP_BYTES) return "This ZIP exceeds the 25 MiB import limit. Choose a smaller package.";
  return "";
}

const ImportZipDialog = ({
  open,
  onClose,
  selectedProjectId,
  activeChange,
  currentUsername,
  onImportedSaved, // optional callback after any save
  onWorkflowStep,
  includeDocuments = false,
  initialZipPath = "",
  autoImportFromPath = false,
  codexHandoffContext = null,
  importContext = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [zipFile, setZipFile] = useState(null);
  const [zipFileError, setZipFileError] = useState("");
  const [zipFileValidation, setZipFileValidation] = useState("idle"); // idle | ready | validated | rejected
  const [lastImportedSource, setLastImportedSource] = useState("");
  // B2: only verified linked-child imports can have persistent audit attempts.
  const auditAttemptRef = useRef(null);
  const [auditState, setAuditState] = useState({ state: "idle", message: "" });
  const [zipPath, setZipPath] = useState("");
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const [saveAndVerifyLoading, setSaveAndVerifyLoading] = useState(false);
  const [saveAndVerifyStatus, setSaveAndVerifyStatus] = useState({
    status: "Not run",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [imported, setImported] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [importedDocuments, setImportedDocuments] = useState([]);
  const [skippedDocuments, setSkippedDocuments] = useState([]);
  const [importedSqlArtifacts, setImportedSqlArtifacts] = useState([]);
  const [sqlArtifactCopyStatus, setSqlArtifactCopyStatus] = useState("");
  const [documentComplianceSummary, setDocumentComplianceSummary] =
    useState(null);
  const [zipStandard, setZipStandard] = useState(null);
  const [riskGate, setRiskGate] = useState(null);
  const [humanApprovedRiskGate, setHumanApprovedRiskGate] = useState(false);
  const [dependencyFindings, setDependencyFindings] = useState([]);
  const [dependencyFindingsAcknowledged, setDependencyFindingsAcknowledged] =
    useState(false);
  const [environmentCapabilityFindings, setEnvironmentCapabilityFindings] =
    useState([]);
  const [dependencyCopyStatus, setDependencyCopyStatus] = useState("");
  const [controlledSaveStatus, setControlledSaveStatus] = useState({ status: "idle", message: "" });
  const controlledSaveAttemptedRef = useRef(false);
  const [autoSaveAfterImportStatus, setAutoSaveAfterImportStatus] = useState({ status: "idle", message: "" });
  const autoSaveAfterImportAttemptedRef = useRef(false);
  const saveAllLastErrorRef = useRef("");
  const [importFilter, setImportFilter] = useState("all");
  const [creatingMap, setCreatingMap] = useState({}); // { [path]: true }
  const [createInfo, setCreateInfo] = useState("");

  const [changeSummary, setChangeSummary] = useState("Imported from ZIP");

  // diff preview state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffErr, setDiffErr] = useState("");
  const [diffText, setDiffText] = useState("");
  const [diffTitle, setDiffTitle] = useState("");

  // NEW: copy status for diff preview
  const [diffCopyStatus, setDiffCopyStatus] = useState("");
  const [allDiffsLoading, setAllDiffsLoading] = useState(false);
  const [allDiffsStatus, setAllDiffsStatus] = useState("");
  const [documentDiffsLoading, setDocumentDiffsLoading] = useState(false);
  const [documentDiffsStatus, setDocumentDiffsStatus] = useState("");
  const [agentHandoffStatus, setAgentHandoffStatus] = useState("");
  const [manualCopyOpen, setManualCopyOpen] = useState(false);
  const [manualCopyTitle, setManualCopyTitle] = useState("");
  const [manualCopyText, setManualCopyText] = useState("");
  const manualCopyRef = useRef(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [documentReviewOpen, setDocumentReviewOpen] = useState(false);
  const [documentReviewLoading, setDocumentReviewLoading] = useState(false);
  const [documentReviewErr, setDocumentReviewErr] = useState("");
  const [documentReviewDoc, setDocumentReviewDoc] = useState(null);
  const [documentReviewDiff, setDocumentReviewDiff] = useState(null);
  const [documentReviewTab, setDocumentReviewTab] = useState("tracked");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewErr, setReviewErr] = useState("");
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewTargetPath, setReviewTargetPath] = useState("");
  const [postImportActions, setPostImportActions] = useState(
    createInitialPostImportStatus,
  );

  const markWorkflowStep = useCallback(
    (stepKey, state = "success", message = "") => {
      if (typeof onWorkflowStep === "function") {
        onWorkflowStep(stepKey, state, message);
      }
    },
    [onWorkflowStep],
  );

  const captureAuditState = (data) => {
    const result = data?.import_audit || { state: "unavailable", message: "No import audit evidence returned by this import route." };
    auditAttemptRef.current = result.state === "recorded" ? result.attempt_id : null;
    setAuditState({ state: result.state, message: result.message || "" });
  };

  const recordSaveAudit = async (attemptId, path, kind, succeeded, error = "") => {
    if (!attemptId || !activeChange?.id || !selectedProjectId) return;
    try {
      const response = await fetch("/api/dev-ide/import-audit/save-outcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attempt_id: attemptId, change_id: activeChange.id,
          project_id: selectedProjectId, file_path: path,
          asset_kind: kind, success: succeeded, error,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.state !== "recorded") {
        if (auditAttemptRef.current !== attemptId) return;
        setAuditState({ state: "unavailable", message: "Save completed independently, but audit recording is unavailable or partial." });
      } else if (result.outcome === "acknowledged_unverified") {
        if (auditAttemptRef.current !== attemptId) return;
        setAuditState({ state: "partial", message: "Save API returned success, but a matching persistent file-save history entry was not found; audit marks it unverified." });
      }
    } catch (_error) {
      if (auditAttemptRef.current !== attemptId) return;
      setAuditState({ state: "unavailable", message: "Save completed independently, but audit recording failed." });
    }
  };

  const importedWithStatus = useMemo(() => {
    return (imported || []).map((f) => ({
      ...f,
      _status: f._status || "ready", // ready | saved | error
      _message: f._message || "",
      change_kind: getCodeImportChangeKind(f),
    }));
  }, [imported]);

  const importedDocumentsWithStatus = useMemo(() => {
    return (importedDocuments || []).map((doc) => ({
      ...doc,
      _status: doc._status || "ready",
      _message: doc._message || "",
      change_kind: getDocumentImportChangeKind(doc),
    }));
  }, [importedDocuments]);

  const filterImportedItems = useCallback(
    (items, assetType) => {
      if (importFilter === "code" && assetType === "docs") return [];
      if (importFilter === "docs" && assetType === "code") return [];
      if (importFilter === "changed_only")
        return items.filter((item) => item.change_kind === "changed");
      if (importFilter === "changed_docs_only")
        return assetType === "docs"
          ? items.filter((item) => item.change_kind === "changed")
          : [];
      if (importFilter === "new")
        return items.filter((item) => item.change_kind === "new");
      if (importFilter === "ready")
        return items.filter((item) => item._status === "ready");
      if (importFilter === "saved")
        return items.filter((item) => item._status === "saved");
      return items;
    },
    [importFilter],
  );

  const filteredImportedFiles = useMemo(
    () => filterImportedItems(importedWithStatus, "code"),
    [filterImportedItems, importedWithStatus],
  );
  const filteredImportedDocuments = useMemo(
    () => filterImportedItems(importedDocumentsWithStatus, "docs"),
    [filterImportedItems, importedDocumentsWithStatus],
  );

  const sqlArtifactsWithPreview = useMemo(
    () =>
      (importedSqlArtifacts || []).map((artifact) => ({
        ...artifact,
        _path: getSqlArtifactPath(artifact),
        _preview: getSqlArtifactPreviewText(artifact),
      })),
    [importedSqlArtifacts],
  );

  const groupedReview = useMemo(
    () =>
      buildGroupedImportReview({
        files: filteredImportedFiles,
        documents: filteredImportedDocuments,
        skipped,
        skippedDocuments,
      }),
    [
      filteredImportedFiles,
      filteredImportedDocuments,
      skipped,
      skippedDocuments,
    ],
  );

  const createableSkippedFiles = useMemo(
    () =>
      (skipped || []).filter(
        (file) =>
          file &&
          file.can_create &&
          file.path &&
          (Object.prototype.hasOwnProperty.call(file, "updated_content") ||
            (isBinaryImportItem(file) && file.content_base64)),
      ),
    [skipped],
  );

  const importSummary = useMemo(() => {
    const binaryFiles = importedWithStatus.filter(isBinaryImportItem);
    const unsupportedBinaryFiles = [
      ...binaryFiles.filter((file) => !canSaveImportItem(file)),
      ...(skipped || []).filter((file) => isBinaryImportItem(file) && !canSaveImportItem(file)),
    ];
    const changedCode = importedWithStatus.filter(
      (file) => file.change_kind === "changed" && !isBinaryImportItem(file),
    ).length;
    const newCode = importedWithStatus.filter(
      (file) => file.change_kind === "new" && !isBinaryImportItem(file),
    ).length;
    const unchangedCode = importedWithStatus.filter(
      (file) => file.change_kind === "unchanged" && !isBinaryImportItem(file),
    ).length;
    const changedDocs = importedDocumentsWithStatus.filter(
      (doc) => doc.change_kind === "changed",
    ).length;
    const unchangedDocs = importedDocumentsWithStatus.filter(
      (doc) => doc.change_kind === "unchanged",
    ).length;
    const newDocs = importedDocumentsWithStatus.filter(
      (doc) => doc.change_kind === "new",
    ).length;
    const readyToSave =
      importedWithStatus.filter(
        (file) => file._status === "ready" && canSaveImportItem(file),
      ).length +
      importedDocumentsWithStatus.filter((doc) => doc._status === "ready")
        .length;
    const savedBinaryCount = binaryFiles.filter((file) => file._status === "saved").length;
    const savedTextFileCount = importedWithStatus.filter(
      (file) => file._status === "saved" && !isBinaryImportItem(file),
    ).length;
    const savedDocumentCount = importedDocumentsWithStatus.filter(
      (doc) => doc._status === "saved",
    ).length;
    const savedCount = savedBinaryCount + savedTextFileCount + savedDocumentCount;
    const errorCount =
      importedWithStatus.filter((file) => file._status === "error").length +
      importedDocumentsWithStatus.filter((doc) => doc._status === "error")
        .length;
    const hashMismatchCount = importedWithStatus.filter((file) => {
      const message = String(file._message || "").toLowerCase();
      return message.includes("hash mismatch") || message.includes("checksum mismatch");
    }).length;
    const missingDocModules = (documentComplianceSummary?.modules || []).filter(
      (entry) => (entry.missing_doc_types || []).length,
    ).length;

    return {
      totalCode: importedWithStatus.filter((file) => !isBinaryImportItem(file)).length,
      totalBinaryFiles: binaryFiles.length,
      changedBinaryFiles: binaryFiles.filter((file) => file.change_kind === "changed").length,
      newBinaryFiles: binaryFiles.filter((file) => file.change_kind === "new").length,
      unchangedBinaryFiles: binaryFiles.filter((file) => file.change_kind === "unchanged").length,
      unsupportedBinaryFiles: unsupportedBinaryFiles.length,
      totalDocs: importedDocumentsWithStatus.length,
      totalSqlArtifacts: sqlArtifactsWithPreview.length,
      changedCode,
      newCode,
      unchangedCode,
      changedDocs,
      unchangedDocs,
      newDocs,
      skippedCode: skipped.length,
      skippedDocs: skippedDocuments.length,
      dependencyFindings: dependencyFindings.length,
      environmentCapabilityFindings: environmentCapabilityFindings.length,
      readyToSave,
      savedCount,
      savedTextFileCount,
      savedBinaryCount,
      savedDocumentCount,
      errorCount,
      hashMismatchCount,
      missingDocModules,
    };
  }, [
    importedWithStatus,
    importedDocumentsWithStatus,
    skipped,
    skippedDocuments,
    dependencyFindings,
    environmentCapabilityFindings,
    documentComplianceSummary,
    sqlArtifactsWithPreview,
  ]);

  const readyChangedImportCount = useMemo(() => {
    const readyChangedCode = importedWithStatus.filter(
      (file) =>
        file._status === "ready" &&
        file.change_kind !== "unchanged" &&
        canSaveImportItem(file),
    ).length;
    const readyChangedDocs = importedDocumentsWithStatus.filter(
      (doc) => doc._status === "ready" && doc.change_kind !== "unchanged",
    ).length;
    return readyChangedCode + readyChangedDocs + createableSkippedFiles.length;
  }, [importedWithStatus, importedDocumentsWithStatus, createableSkippedFiles]);

  const reviewCoverage = useMemo(
    () => buildImportReviewCoverage(importSummary),
    [importSummary],
  );

  const reviewCoverageHint = useMemo(
    () => getImportReviewCoverageHint(importSummary),
    [importSummary],
  );

  const highRiskDependencyFindings = useMemo(
    () =>
      (dependencyFindings || []).filter(
        (finding) => String(finding.severity || "").toLowerCase() === "high",
      ),
    [dependencyFindings],
  );

  const dependencyFixSummary = useMemo(() => {
    if (!(dependencyFindings || []).length && !(environmentCapabilityFindings || []).length) return "";
    const lines = [
      "Dependency & Environment Checks",
      "",
    ];
    if ((dependencyFindings || []).length) {
      lines.push("Missing dependency findings:");
      (dependencyFindings || []).forEach((finding) => {
        lines.push(
          `- [${finding.severity || "review"}] ${finding.ecosystem || "unknown"} ${finding.package_name || "(unknown package)"} in ${finding.source_file_path || "(unknown source)"}: ${finding.required_manifest_action || finding.message || "Review dependency manifest update."}`,
        );
      });
      lines.push("");
    }
    if ((environmentCapabilityFindings || []).length) {
      lines.push("Browser environment capability findings:");
      (environmentCapabilityFindings || []).forEach((finding) => {
        lines.push(
          `- [${finding.severity || "review"}] ${finding.label || finding.capability || "Browser capability"} in ${finding.source_file_path || "(unknown source)"}: ${finding.environment_requirement || finding.message || "Review target browser/device requirement."} Smoke test: ${finding.manual_smoke_test || "Record pass/fail/notes in the Full Change smoke-test checklist."}`,
        );
      });
      lines.push("");
    }
    lines.push("");
    lines.push(
      "Source code may be saved before this is complete, but the change is not build-verified until dependency manifests are updated, dependencies are installed, and post-import verification passes.",
    );
    lines.push(
      "Browser capability note: camera, clipboard, geolocation, notifications, and service-worker features may require HTTPS or localhost plus browser permission checks.",
    );
    return lines.join("\n");
  }, [dependencyFindings, environmentCapabilityFindings]);


  const saveAutomationBlockedByRiskGate = useMemo(() => {
    if (!riskGate) return false;
    // Medium-risk / require_human_review findings must remain visible, but they
    // should not stop the Codex returned-package save pipeline. Only explicit
    // block_automation decisions are hard stops for Save All automation.
    return riskGate.decision === "block_automation";
  }, [riskGate]);

  const controlledSaveEnabled = Boolean(codexHandoffContext && codexHandoffContext.controlledSaveEnabled);
  const shouldAutoSaveAfterImport = Boolean(importContext && importContext.autoSaveAfterImport === true);

  const agentImportReview = useMemo(() => {
    const importedCodeItems = (importedWithStatus || []).map((file) => ({
      path: getImportReviewPath(file),
      assetType: "code",
      changeKind: file.change_kind,
    }));
    const importedDocItems = (importedDocumentsWithStatus || []).map((doc) => ({
      path: getImportReviewPath(doc),
      assetType: "documentation",
      changeKind: doc.change_kind,
    }));
    const skippedCodeItems = (skipped || []).map((file) => ({
      path: getImportReviewPath(file),
      assetType: "code",
      changeKind: "skipped",
    }));
    const skippedDocItems = (skippedDocuments || []).map((doc) => ({
      path: getImportReviewPath(doc),
      assetType: "documentation",
      changeKind: "skipped",
    }));

    const importedItems = [...importedCodeItems, ...importedDocItems];
    const allReviewItems = [
      ...importedItems,
      ...skippedCodeItems,
      ...skippedDocItems,
    ].filter((item) => item.path);
    const newItems = importedItems.filter((item) => item.changeKind === "new");
    const modifiedItems = importedItems.filter(
      (item) => item.changeKind === "changed",
    );
    const changedItems = [...newItems, ...modifiedItems].sort((a, b) =>
      a.path.localeCompare(b.path),
    );
    const outsideStructureItems = allReviewItems
      .filter((item) => !isExpectedProjectModulePath(item.path))
      .sort((a, b) => a.path.localeCompare(b.path));

    return {
      outsideStructurePaths: outsideStructureItems.map((item) => item.path),
      hasNoChangedFiles: changedItems.length === 0,
    };
  }, [
    importedWithStatus,
    importedDocumentsWithStatus,
    skipped,
    skippedDocuments,
  ]);

  const hasSkippedFiles = (skipped || []).length > 0;
  const hasSkippedDocuments = (skippedDocuments || []).length > 0;

  const reviewableFiles = useMemo(() => {
    const byPath = new Map();

    (importedWithStatus || []).forEach((file) => {
      if (!file || !file.path) return;
      byPath.set(file.path, {
        file_id: file.file_id,
        path: file.path,
        module: file.module,
        original_content: file.original_content || "",
        updated_content: file.updated_content || "",
      });
    });

    (skipped || []).forEach((file) => {
      if (!file || !file.path) return;
      if (!file.can_create) return;
      if (!Object.prototype.hasOwnProperty.call(file, "updated_content"))
        return;
      if (byPath.has(file.path)) return;
      byPath.set(file.path, {
        file_id: file.file_id || null,
        path: file.path,
        module: file.module || "",
        original_content: file.original_content || "",
        updated_content: file.updated_content || "",
      });
    });

    return Array.from(byPath.values());
  }, [importedWithStatus, skipped]);

  useEffect(() => {
    if (!importedWithStatus.length) {
      setReviewTargetPath("");
      return;
    }

    const stillExists = importedWithStatus.some(
      (f) => f.path === reviewTargetPath,
    );
    if (!reviewTargetPath || !stillExists) {
      setReviewTargetPath(importedWithStatus[0].path || "");
    }
  }, [importedWithStatus, reviewTargetPath]);

  useEffect(() => {
    if (!manualCopyOpen || !manualCopyRef.current) return;

    const timer = window.setTimeout(() => {
      try {
        manualCopyRef.current.focus();
        manualCopyRef.current.select();
        manualCopyRef.current.setSelectionRange(0, manualCopyText.length);
      } catch (e) {
        // no-op
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [manualCopyOpen, manualCopyText]);

  useEffect(() => {
    if (!open) return;
    if (!initialZipPath) return;
    setZipPath(initialZipPath);
  }, [open, initialZipPath]);

  const resetState = () => {
    setZipFile(null);
    setZipFileError("");
    setZipFileValidation("idle");
    setLastImportedSource("");
    auditAttemptRef.current = null;
    setAuditState({ state: "idle", message: "" });
    setZipPath("");
    setSaveAllLoading(false);
    setLoading(false);
    setErr("");
    setImported([]);
    setSkipped([]);
    setImportedDocuments([]);
    setSkippedDocuments([]);
    setImportedSqlArtifacts([]);
    setSqlArtifactCopyStatus("");
    setDocumentComplianceSummary(null);
    setZipStandard(null);
    setRiskGate(null);
    setHumanApprovedRiskGate(false);
    setDependencyFindings([]);
    setDependencyFindingsAcknowledged(false);
    setEnvironmentCapabilityFindings([]);
    setDependencyCopyStatus("");
    setControlledSaveStatus({ status: "idle", message: "" });
    controlledSaveAttemptedRef.current = false;
    setAutoSaveAfterImportStatus({ status: "idle", message: "" });
    autoSaveAfterImportAttemptedRef.current = false;
    saveAllLastErrorRef.current = "";
    setImportFilter("all");
    setCreatingMap({});
    setCreateInfo("");
    setDiffOpen(false);
    setDiffLoading(false);
    setDiffErr("");
    setDiffText("");
    setDiffTitle("");
    setDiffCopyStatus(""); // NEW
    setAllDiffsLoading(false);
    setAllDiffsStatus("");
    setAgentHandoffStatus("");
    setManualCopyOpen(false);
    setManualCopyTitle("");
    setManualCopyText("");
    setReviewOpen(false);
    setDocumentReviewOpen(false);
    setDocumentReviewLoading(false);
    setDocumentReviewErr("");
    setDocumentReviewDoc(null);
    setDocumentReviewDiff(null);
    setDocumentReviewTab("tracked");
    setReviewLoading(false);
    setReviewErr("");
    setReviewResult(null);
    setReviewTargetPath("");
    setPostImportActions(createInitialPostImportStatus());
    setChangeSummary("Imported from ZIP");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const applyImportResponse = (data, options = {}) => {
    const list = (data.imported || []).map((f) => ({
      ...f,
      _status: "ready",
      _message: "",
    }));
    setImported(list);
    setLastImportedSource(options.sourceName || "");
    captureAuditState(data);
    setSkipped(data.skipped || []);
    setImportedDocuments(
      (data.imported_documents || []).map((doc) => ({
        ...doc,
        _status: "ready",
        _message: "",
      })),
    );
    setSkippedDocuments(data.skipped_documents || []);
    setImportedSqlArtifacts(data.sql_artifacts || data.imported_sql_artifacts || []);
    setSqlArtifactCopyStatus("");
    setDocumentComplianceSummary(data.document_compliance_summary || null);
    setZipStandard(data.zip_standard || null);
    setRiskGate(data.risk_gate || null);
    setDependencyFindings(data.dependency_findings || []);
    setDependencyFindingsAcknowledged(false);
    setEnvironmentCapabilityFindings(data.environment_capability_findings || []);
    setDependencyCopyStatus("");
    if (!options.preserveSaveAutomationState) {
      setHumanApprovedRiskGate(false);
      setControlledSaveStatus({ status: "idle", message: "" });
      controlledSaveAttemptedRef.current = false;
      setAutoSaveAfterImportStatus(
        shouldAutoSaveAfterImport
          ? { status: "waiting", message: "Auto-save is enabled for this returned package and will run after the import preview is ready." }
          : { status: "idle", message: "" },
      );
      autoSaveAfterImportAttemptedRef.current = false;
      saveAllLastErrorRef.current = "";
    }
    setCreateInfo("");
    setReviewErr("");
    setReviewResult(null);
    setPostImportActions(createInitialPostImportStatus());
    setAgentHandoffStatus("");
    markWorkflowStep(
      "packageImported",
      "success",
      data.codex_handoff
        ? `Imported Codex return into review only: ${(data.imported || []).length} code file(s), ${(data.imported_documents || []).length} document(s), and ${(data.sql_artifacts || data.imported_sql_artifacts || []).length} SQL artifact(s). No files were saved.`
        : `Imported ${(data.imported || []).length} code file(s), ${(data.imported_documents || []).length} document(s), and ${(data.sql_artifacts || data.imported_sql_artifacts || []).length} SQL artifact(s).`,
    );
  };

  const clearImportPreview = (options = {}) => {
    setImported([]);
    setSkipped([]);
    setImportedDocuments([]);
    setSkippedDocuments([]);
    setImportedSqlArtifacts([]);
    setSqlArtifactCopyStatus("");
    setDocumentComplianceSummary(null);
    setZipStandard(null);
    setLastImportedSource("");
    auditAttemptRef.current = null;
    setAuditState({ state: "idle", message: "" });
    setRiskGate(null);
    setDependencyFindings([]);
    setDependencyFindingsAcknowledged(false);
    setEnvironmentCapabilityFindings([]);
    setDependencyCopyStatus("");
    if (!options.preserveSaveAutomationState) {
      setHumanApprovedRiskGate(false);
      setControlledSaveStatus({ status: "idle", message: "" });
      controlledSaveAttemptedRef.current = false;
      setAutoSaveAfterImportStatus({ status: "idle", message: "" });
      autoSaveAfterImportAttemptedRef.current = false;
      saveAllLastErrorRef.current = "";
    }
  };

  const handlePickedZip = (event) => {
    const chosen = event.target.files && event.target.files[0];
    // Reset native input so selecting the same filename again triggers onChange.
    event.target.value = "";
    if (!chosen) return;
    const error = validatePickedZip(chosen);
    clearImportPreview(); // Never leave a stale preview/save candidate for a different selected ZIP.
    setZipFile(chosen);
    setZipFileError(error);
    setZipFileValidation(error ? "rejected" : "ready");
    setErr("");
  };

  const handleUpload = async (options = {}) => {
    const validationError = validatePickedZip(zipFile);
    if (validationError) {
      setZipFileError(validationError);
      setZipFileValidation("rejected");
      return;
    }

    setLoading(true);
    setErr("");
    clearImportPreview(options);

    try {
      const form = new FormData();
      form.append("zip_file", zipFile);
      if (selectedProjectId) form.append("project_id", selectedProjectId);
      if (activeChange?.id) form.append("audit_change_id", activeChange.id);
      form.append("audit_username", currentUsername || "");
      if (importContext?.fullChangeId || activeChange?.full_change_id) {
        form.append("audit_full_change_id", importContext?.fullChangeId || activeChange.full_change_id);
      }

      const res = await fetch("/api/dev-ide/import-zip", {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!data.success) {
        captureAuditState(data);
        throw new Error(data.error || "Failed to import zip.");
      }

      applyImportResponse(data, { ...options, sourceName: zipFile.name });
      setZipFileValidation("validated");
    } catch (e) {
      setZipFileValidation("rejected");
      setErr(e.message);
      markWorkflowStep("packageImported", "failure", e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromPath = async (options = {}) => {
    const trimmedPath = (zipPath || "").trim();
    if (!trimmedPath) {
      setErr("Enter a ZIP file path first.");
      return;
    }

    setLoading(true);
    setErr("");
    clearImportPreview(options);

    try {
      const isCodexHandoffImport = Boolean(codexHandoffContext && codexHandoffContext.devIdeChangeId);
      const res = await fetch(
        isCodexHandoffImport
          ? "/api/dev-ide/codex-handoff/import-returned-zip"
          : "/api/dev-ide/import-zip-from-path",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isCodexHandoffImport
              ? {
                  project_id: selectedProjectId || codexHandoffContext.projectId || null,
                  full_change_id: codexHandoffContext.fullChangeId || null,
                  dev_ide_change_id: codexHandoffContext.devIdeChangeId,
                  run_id: codexHandoffContext.runId || null,
                  filename: codexHandoffContext.filename || null,
                }
              : {
                  zip_path: trimmedPath,
                  project_id: selectedProjectId || null,
                  audit_change_id: activeChange?.id || null,
                  audit_username: currentUsername || null,
                  audit_full_change_id: importContext?.fullChangeId || activeChange?.full_change_id || null,
                },
          ),
        },
      );
      const data = await res.json();
      if (!data.success) {
        captureAuditState(data);
        throw new Error(data.error || "Failed to import zip from path.");
      }

      applyImportResponse(data, { ...options, sourceName: trimmedPath });
      setZipFile(null);
      setZipFileError("");
      setZipFileValidation("idle");
    } catch (e) {
      setErr(e.message);
      markWorkflowStep("packageImported", "failure", e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !autoImportFromPath || !initialZipPath || loading) return;
    if (imported.length || importedDocuments.length || skipped.length || skippedDocuments.length) return;

    const timer = window.setTimeout(() => {
      handleImportFromPath();
    }, 100);

    return () => window.clearTimeout(timer);
    // Intentionally constrained to the initial open/path trigger to avoid repeated auto-import loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoImportFromPath, initialZipPath]);

  const createMissingImportFiles = async (filesToCreate, options = {}) => {
    const createableFiles = (filesToCreate || []).filter((file) => {
      if (!file || !file.path || !file.can_create) return false;

      if (isBinaryImportItem(file)) {
        return Boolean(file.content_base64);
      }

      return Object.prototype.hasOwnProperty.call(file, "updated_content");
    });
    if (!createableFiles.length) return true;

    const hasImportSource = Boolean(zipFile || (zipPath || "").trim());
    if (!hasImportSource) {
      setErr("Choose a ZIP file or enter an import path first.");
      return false;
    }

    const creatingPaths = createableFiles.map((file) => file.path);
    setCreatingMap((prev) => {
      const next = { ...(prev || {}) };
      creatingPaths.forEach((p) => {
        next[p] = true;
      });
      return next;
    });
    setCreateInfo("");

    try {
      const body = {
        project_id: selectedProjectId || null,
        change_id: activeChange && activeChange.id ? activeChange.id : null,
        username: currentUsername || "dev-ide",
        files: createableFiles.map((file) => ({
          path: file.path,
          updated_content: file.updated_content,
          is_binary: Boolean(file.is_binary),
          content_base64: file.content_base64 || undefined,
          sha256: file.sha256 || undefined,
          zip_member_path: file.zip_member_path || file.path,
        })),
      };

      const res = await fetch("/api/dev-ide/import-zip-create-missing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to create missing file.");
      }

      const createdCount = (data.created || []).length;
      const skippedCreateCount = (data.skipped || []).length;
      if (skippedCreateCount) {
        const skippedPaths = (data.skipped || [])
          .map((item) => `${item.path || "new file"}: ${item.reason || "skipped"}`)
          .join("; ");
        throw new Error(`Create missing files skipped ${skippedCreateCount} item(s): ${skippedPaths}`);
      }

      const scanSummary = [];
      if (data.scans && data.scans.backend) scanSummary.push("backend scan");
      if (data.scans && data.scans.frontend) scanSummary.push("frontend scan");

      setCreateInfo(
        `${options.successPrefix || "Created"} ${createdCount || createableFiles.length} file(s) and ran ${
          scanSummary.join(" + ") || "scan"
        }. Re-importing ZIP...`,
      );

      // Re-run import so the new file should now appear in the imported list.
      if (zipFile) {
        await handleUpload({
          preserveSaveAutomationState: options.preserveSaveAutomationState,
        });
      } else {
        await handleImportFromPath({
          preserveSaveAutomationState: options.preserveSaveAutomationState,
        });
      }
      return true;
    } catch (e) {
      const label = createableFiles.length === 1 ? createableFiles[0].path : `${createableFiles.length} files`;
      setCreateInfo(`Create failed for ${label}: ${e.message}`);
      setErr(e.message || "Failed to create missing files.");
      return false;
    } finally {
      setCreatingMap((prev) => {
        const next = { ...(prev || {}) };
        creatingPaths.forEach((p) => {
          delete next[p];
        });
        return next;
      });
    }
  };

  const handleCreateMissingFile = async (skippedItem) => {
    if (!skippedItem || !skippedItem.path) return;
    await createMissingImportFiles([skippedItem]);
  };

  const openManualCopyDialog = (title, text) => {
    setManualCopyTitle(title || "Copy diff");
    setManualCopyText(text || "");
    setManualCopyOpen(true);
  };

  const isLikelyIOSWebKitClipboardEnvironment = () => {
    if (typeof navigator === "undefined") return false;

    const ua = navigator.userAgent || "";
    const vendor = navigator.vendor || "";
    const platform = navigator.platform || "";
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    const isIOSDevice =
      /iPad|iPhone|iPod/.test(ua) ||
      (/Mac/.test(platform) && maxTouchPoints > 1);
    const isAppleWebKit = /Apple/i.test(vendor) || /Safari/i.test(ua);

    return isIOSDevice && isAppleWebKit;
  };

  const tryExecCommandCopy = (text) => {
    if (
      typeof document === "undefined" ||
      typeof document.execCommand !== "function"
    ) {
      return false;
    }

    let ta = null;
    try {
      ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "-9999px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      return !!document.execCommand("copy");
    } catch (e) {
      return false;
    } finally {
      if (ta && ta.parentNode) {
        ta.parentNode.removeChild(ta);
      }
    }
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return { ok: false, mode: "empty" };

    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      typeof window !== "undefined" &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
      return { ok: true, mode: "clipboard" };
    }

    if (!isLikelyIOSWebKitClipboardEnvironment() && tryExecCommandCopy(text)) {
      return { ok: true, mode: "execCommand" };
    }

    return { ok: false, mode: "manual" };
  };

  const copyDependencyFixSummary = async () => {
    if (!dependencyFixSummary) return;
    try {
      const result = await copyTextToClipboard(dependencyFixSummary);
      if (result.ok) {
        setDependencyCopyStatus("Dependency fix summary copied");
      } else {
        openManualCopyDialog("Dependency fix summary", dependencyFixSummary);
        setDependencyCopyStatus("Manual copy mode opened");
      }
    } catch (e) {
      openManualCopyDialog("Dependency fix summary", dependencyFixSummary);
      setDependencyCopyStatus("Manual copy mode opened");
    }
  };

  const buildDiffPayload = (file) => ({
    original_content: file.original_content || "",
    updated_content: file.updated_content || "",
    file_id: file.file_id,
    file_path: file.path,
    change_summary: changeSummary || "",
    username: currentUsername || "unknown",
    change_id: activeChange && activeChange.id ? activeChange.id : null,
    project_id: selectedProjectId || null,
  });

  const buildBinaryAssetDiffSummary = (file = {}) => {
    return [
      "Binary asset - text diff not available.",
      `Path: ${file.path || "unknown"}`,
      `Type: ${getBinaryExtension(file)}`,
      `Size: ${formatBytes(file.size_bytes)}`,
      `SHA256: ${file.sha256 || "not provided"}`,
      file.original_sha256 ? `Original SHA256: ${file.original_sha256}` : null,
      `Status: ${getBinaryImportStatus(file) || "changed"}`,
      !canSaveImportItem(file) ? `Blocked: ${file.reason || "unsupported binary asset"}` : null,
    ].filter(Boolean).join("\n");
  };

  const fetchDiffTextForFile = async (file) => {
    if (isBinaryImportItem(file)) {
      return buildBinaryAssetDiffSummary(file);
    }
    const res = await fetch("/api/dev-ide/preview-diff", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildDiffPayload(file)),
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(
        data.error || `Failed to generate diff for ${file.path || "file"}.`,
      );
    }
    return data.diff || "";
  };

  const formatDiffBlock = (file, diff) => {
    const lines = [`# PATH: ${file.path || "unknown"}`];
    if (file.module) {
      lines.push(`# MODULE: ${file.module}`);
    }
    lines.push("");
    lines.push(diff || "(no diff)");
    return lines.join("\n");
  };

  const joinCombinedDiffBlocks = (chunks) =>
    chunks.join("\n\n" + "=".repeat(80) + "\n\n");

  const buildAllDiffsText = async () => {
    const files = importedWithStatus || [];
    const chunks = [];

    for (const file of files) {
      const diff = await fetchDiffTextForFile(file);
      chunks.push(formatDiffBlock(file, diff));
    }

    return joinCombinedDiffBlocks(chunks);
  };

  const getChangedImportedDocuments = () =>
    (importedDocumentsWithStatus || []).filter(
      (doc) => doc.change_kind === "changed" || doc.change_kind === "new",
    );

  const formatDocumentDiffBlock = (doc, diff) => {
    const lines = [`# PATH: ${doc.path || doc.title || "unknown"}`];
    const modules = (doc.module_names || []).filter(Boolean);
    if (modules.length) {
      lines.push(`# MODULE: ${modules.join(", ")}`);
    }
    lines.push("");
    lines.push(diff || "(no diff)");
    return lines.join("\n");
  };

  const buildAllDocumentDiffsText = async () => {
    const docs = getChangedImportedDocuments();
    const chunks = [];

    for (const doc of docs) {
      const diff = await fetchDocumentDiff(doc);
      chunks.push(
        formatDocumentDiffBlock(doc, (diff || {}).unified_diff || "(no diff)"),
      );
    }

    return joinCombinedDiffBlocks(chunks);
  };

  const buildAllCodeAndDocumentDiffsText = async () => {
    const hasCodeDiffs = importedWithStatus.length > 0;
    const hasDocumentDiffs = getChangedImportedDocuments().length > 0;

    if (hasCodeDiffs && !hasDocumentDiffs) {
      return buildAllDiffsText();
    }

    if (!hasCodeDiffs && hasDocumentDiffs) {
      return buildAllDocumentDiffsText();
    }

    const chunks = [];
    const codeDiffsText = await buildAllDiffsText();
    if (codeDiffsText) {
      chunks.push(["# CODE DIFFS", "", codeDiffsText].join("\n"));
    }

    const documentDiffsText = await buildAllDocumentDiffsText();
    if (documentDiffsText) {
      chunks.push(["# DOCUMENT DIFFS", "", documentDiffsText].join("\n"));
    }

    return joinCombinedDiffBlocks(chunks);
  };

  const handlePreviewDiff = async (file) => {
    if (!file) return;
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(file.path || "Diff");
    setDiffCopyStatus(""); // NEW
    setAllDiffsLoading(false);
    setAllDiffsStatus("");

    try {
      const diff = await fetchDiffTextForFile(file);
      setDiffText(diff);
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  // NEW: copy current preview diff to clipboard
  const handleCopyDiff = async () => {
    const path = diffTitle || "";
    const diff = diffText || "";
    if (!path && !diff) return;

    const textToCopy = `# PATH: ${path}\n\n${diff}`;

    try {
      const result = await copyTextToClipboard(textToCopy);
      if (result.ok) {
        setDiffCopyStatus("Copied to clipboard");
        setTimeout(() => setDiffCopyStatus(""), 2000);
        return;
      }

      openManualCopyDialog(path || "Diff", textToCopy);
      setDiffCopyStatus("Manual copy mode opened");
      setTimeout(() => setDiffCopyStatus(""), 2500);
    } catch (e) {
      openManualCopyDialog(path || "Diff", textToCopy);
      setDiffCopyStatus("Manual copy mode opened");
      setTimeout(() => setDiffCopyStatus(""), 2500);
    }
  };

  const handleViewSqlArtifact = (artifact) => {
    if (!artifact) return;
    const path = getSqlArtifactPath(artifact);
    const preview = getSqlArtifactPreviewText(artifact);
    const lines = [
      `# SQL ARTIFACT: ${path}`,
      `# Type: ${artifact.sql_type || artifact.artifact_type || "sql"}`,
      `# Full Change: ${artifact.full_change_id || "not linked"}`,
      `# DEV IDE Change: ${artifact.dev_ide_change_id || "not linked"}`,
      `# Review status: ${artifact.review_status || "pending_review"}`,
      `# Execution status: ${artifact.execution_status || "not_executed"}`,
      "",
      "-- Stored for human review only. Import Review does not execute SQL automatically.",
      "",
      preview || "-- SQL content preview was not returned by the backend for this artifact.",
    ];
    openManualCopyDialog(path, lines.join("\n"));
  };

  const handleCopySqlArtifact = async (artifact) => {
    if (!artifact) return;
    const path = getSqlArtifactPath(artifact);
    const preview = getSqlArtifactPreviewText(artifact);
    const textToCopy = [
      `# SQL ARTIFACT: ${path}`,
      `# Type: ${artifact.sql_type || artifact.artifact_type || "sql"}`,
      `# Full Change: ${artifact.full_change_id || "not linked"}`,
      `# DEV IDE Change: ${artifact.dev_ide_change_id || "not linked"}`,
      `# Review status: ${artifact.review_status || "pending_review"}`,
      `# Execution status: ${artifact.execution_status || "not_executed"}`,
      "",
      "-- Stored for human review only. Import Review does not execute SQL automatically.",
      "",
      preview || "-- SQL content preview was not returned by the backend for this artifact.",
    ].join("\n");

    try {
      const result = await copyTextToClipboard(textToCopy);
      if (result.ok) {
        setSqlArtifactCopyStatus(`Copied SQL artifact: ${path}`);
        setTimeout(() => setSqlArtifactCopyStatus(""), 2500);
        return;
      }
      openManualCopyDialog(path, textToCopy);
      setSqlArtifactCopyStatus("Manual copy mode opened");
      setTimeout(() => setSqlArtifactCopyStatus(""), 3000);
    } catch (e) {
      openManualCopyDialog(path, textToCopy);
      setSqlArtifactCopyStatus("Manual copy mode opened");
      setTimeout(() => setSqlArtifactCopyStatus(""), 3000);
    }
  };

  const handlePreviewAllDiffs = async () => {
    if (!importedWithStatus.length) return;

    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(
      `All imported diffs (${importedWithStatus.length} file${importedWithStatus.length === 1 ? "" : "s"})`,
    );
    setDiffCopyStatus("");
    setAllDiffsStatus("");

    try {
      const allDiffsText = await buildAllDiffsText();
      setDiffText(allDiffsText || "(no diffs)");
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const handlePreviewAllDocumentDiffs = async () => {
    const changedDocuments = getChangedImportedDocuments();
    if (!changedDocuments.length) return;

    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(
      `All imported document diffs (${changedDocuments.length} doc${changedDocuments.length === 1 ? "" : "s"})`,
    );
    setDiffCopyStatus("");
    setDocumentDiffsStatus("");

    try {
      const allDocumentDiffsText = await buildAllDocumentDiffsText();
      setDiffText(allDocumentDiffsText || "(no diffs)");
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const buildDocsChangedLabel = () => {
    const changedDocs = importedDocumentsWithStatus.filter(
      (doc) => doc.change_kind === "changed" || doc.change_kind === "new",
    ).length;

    if (!changedDocs) {
      return includeDocuments
        ? "No document updates detected in imported package"
        : "Not specified";
    }

    return `Yes - ${changedDocs} imported document update${changedDocs === 1 ? "" : "s"}`;
  };

  const handleCopyDiffReviewPrompt = async () => {
    if (!importedWithStatus.length) return;

    setAllDiffsLoading(true);
    setAllDiffsStatus("");

    let allDiffsText = "";

    try {
      allDiffsText = await buildAllDiffsText();
      const prompt = buildDiffReviewPrompt({
        projectId: selectedProjectId || "",
        changeId: activeChange && activeChange.id ? activeChange.id : null,
        changeTitle: activeChange?.title || changeSummary || "",
        changeDescription: activeChange?.description || changeSummary || "",
        docsChanged: buildDocsChangedLabel(),
        changeSummary,
        importedFileCount: importedWithStatus.length,
        allDiffsText,
      });
      const result = await copyTextToClipboard(prompt);
      if (result.ok) {
        setAllDiffsStatus("GPT review prompt copied to clipboard");
        setTimeout(() => setAllDiffsStatus(""), 2500);
        return;
      }

      openManualCopyDialog(
        `GPT review prompt (${importedWithStatus.length})`,
        prompt,
      );
      setAllDiffsStatus("Manual copy mode opened");
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } catch (e) {
      if (allDiffsText) {
        const prompt = buildDiffReviewPrompt({
          projectId: selectedProjectId || "",
          changeId: activeChange && activeChange.id ? activeChange.id : null,
          changeTitle: activeChange?.title || changeSummary || "",
          changeDescription: activeChange?.description || changeSummary || "",
          docsChanged: buildDocsChangedLabel(),
          changeSummary,
          importedFileCount: importedWithStatus.length,
          allDiffsText,
        });
        openManualCopyDialog(
          `GPT review prompt (${importedWithStatus.length})`,
          prompt,
        );
        setAllDiffsStatus("Manual copy mode opened");
      } else {
        setAllDiffsStatus(e.message || "Copy GPT review prompt failed");
      }
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } finally {
      setAllDiffsLoading(false);
    }
  };

  const getImportPackageName = () => {
    if (lastImportedSource) return lastImportedSource;
    if (zipFile && zipFile.name) return zipFile.name;
    if (zipPath && zipPath.trim()) return zipPath.trim();
    return "Not available";
  };

  const buildImportVerificationLines = () => {
    const lines = Object.entries(POST_IMPORT_ACTIONS).map(([key, action]) => {
      const state = postImportActions[key] || {};
      const status = state.status || "Not run";
      const detail = state.error || state.output || "";
      const firstDetailLine = String(detail || "").split("\n").find(Boolean) || "";
      return `${action.label}: ${status}${firstDetailLine ? ` - ${firstDetailLine}` : ""}`;
    });

    if (saveAndVerifyStatus.status !== "Not run") {
      lines.unshift(`Save All and Verify: ${saveAndVerifyStatus.status}${saveAndVerifyStatus.message ? ` - ${saveAndVerifyStatus.message}` : ""}`);
    }

    return lines.length ? lines : ["No verification actions recorded."];
  };

  const buildImportWarnings = () => {
    const warnings = [];
    if (hasSkippedFiles) warnings.push(`${skipped.length} code file(s) skipped during import review.`);
    if (hasSkippedDocuments) warnings.push(`${skippedDocuments.length} document(s) skipped during import review.`);
    if (importSummary.missingDocModules) warnings.push(`${importSummary.missingDocModules} module scope(s) still have missing standard docs.`);
    Object.entries(postImportActions || {}).forEach(([key, state]) => {
      if (state?.status === "Skipped") {
        warnings.push(`${POST_IMPORT_ACTIONS[key]?.label || key} skipped: ${state.output || "not configured"}`);
      }
    });
    return warnings;
  };

  const buildImportErrors = () => {
    const errors = [];
    if (err) errors.push(err);
    importedWithStatus.forEach((file) => {
      if (file._status === "error") errors.push(`${file.path}: ${file._message || "save error"}`);
    });
    importedDocumentsWithStatus.forEach((doc) => {
      if (doc._status === "error") errors.push(`${doc.path || doc.title}: ${doc._message || "save error"}`);
    });
    Object.entries(postImportActions || {}).forEach(([key, state]) => {
      if (state?.status === "Failed") {
        errors.push(`${POST_IMPORT_ACTIONS[key]?.label || key}: ${state.error || "failed"}`);
      }
    });
    if (saveAndVerifyStatus.status === "Failed") {
      errors.push(`Save All and Verify: ${saveAndVerifyStatus.message || "failed"}`);
    }
    return errors;
  };

  const buildImportAgentHandoffSummary = async ({ includeDiffs = false } = {}) => {
    const changedCodePaths = importedWithStatus
      .filter((file) => file.change_kind === "changed" || file.change_kind === "new")
      .map((file) => file.path)
      .filter(Boolean);
    const changedDocumentPaths = importedDocumentsWithStatus
      .filter((doc) => doc.change_kind === "changed" || doc.change_kind === "new")
      .map((doc) => doc.path || doc.title)
      .filter(Boolean);

    let codeDiffsText = "";
    let documentDiffsText = "";
    if (includeDiffs) {
      if (importedWithStatus.length) {
        codeDiffsText = await buildAllDiffsText();
      }
      if (getChangedImportedDocuments().length) {
        documentDiffsText = await buildAllDocumentDiffsText();
      }
    }

    return buildAgentHandoffSummary({
      phase: "Import Review / Verification",
      projectId: selectedProjectId || "",
      codeModule: "Imported ZIP package",
      includeDocs: includeDocuments || importedDocumentsWithStatus.length > 0,
      docsMode: includeDocuments ? "imported package" : "none",
      docModules: includeDocuments ? ["Imported document modules"] : [],
      packageName: getImportPackageName(),
      changedCodePaths,
      changedDocumentPaths,
      verificationLines: buildImportVerificationLines(),
      warnings: buildImportWarnings(),
      errors: buildImportErrors(),
      recommendedNextStep: buildImportErrors().length
        ? "Resolve the failed save or verification step, then rerun the import verification workflow."
        : importSummary.readyToSave
          ? "Save remaining changed items, then run Save All and Verify."
          : "Use the copied summary for agent handoff or promote the verified package to the next environment.",
      codeDiffsText,
      documentDiffsText,
      includeDiffs,
    });
  };

  const handleCopyAgentHandoffSummary = async (includeDiffs = false) => {
    if (!importedWithStatus.length && !importedDocumentsWithStatus.length && !hasSkippedFiles && !hasSkippedDocuments) return;

    setAllDiffsLoading(includeDiffs);
    setDocumentDiffsLoading(includeDiffs);
    setAgentHandoffStatus("");

    let handoffText = "";
    try {
      handoffText = await buildImportAgentHandoffSummary({ includeDiffs });
      const result = await copyTextToClipboard(handoffText);
      if (result.ok) {
        setAgentHandoffStatus(includeDiffs ? "Agent handoff summary with diffs copied" : "Agent handoff summary copied");
        markWorkflowStep(
          "verificationSummaryCopied",
          "success",
          includeDiffs
            ? "Agent handoff summary with diffs copied from Import Review modal."
            : "Agent handoff summary copied from Import Review modal.",
        );
        setTimeout(() => setAgentHandoffStatus(""), 3000);
        return;
      }

      openManualCopyDialog(
        includeDiffs ? "Agent handoff summary with diffs" : "Agent handoff summary",
        handoffText,
      );
      setAgentHandoffStatus("Manual copy mode opened");
      setTimeout(() => setAgentHandoffStatus(""), 3000);
    } catch (e) {
      if (handoffText) {
        openManualCopyDialog(
          includeDiffs ? "Agent handoff summary with diffs" : "Agent handoff summary",
          handoffText,
        );
        setAgentHandoffStatus("Manual copy mode opened");
      } else {
        setAgentHandoffStatus(e.message || "Copy agent handoff summary failed");
      }
      markWorkflowStep("verificationSummaryCopied", "failure", e.message || "Copy agent handoff summary failed.");
      setTimeout(() => setAgentHandoffStatus(""), 3000);
    } finally {
      setAllDiffsLoading(false);
      setDocumentDiffsLoading(false);
    }
  };

  const handleCopyAllDiffs = async () => {
    const changedDocuments = getChangedImportedDocuments();
    if (!importedWithStatus.length && !changedDocuments.length) return;

    setAllDiffsLoading(true);
    setDocumentDiffsLoading(Boolean(changedDocuments.length));
    setAllDiffsStatus("");
    setDocumentDiffsStatus("");

    let allDiffsText = "";
    const totalDiffItems = importedWithStatus.length + changedDocuments.length;

    try {
      allDiffsText = await buildAllCodeAndDocumentDiffsText();
      const result = await copyTextToClipboard(allDiffsText);
      if (result.ok) {
        setAllDiffsStatus("All code and document diffs copied to clipboard");
        setTimeout(() => setAllDiffsStatus(""), 2500);
        return;
      }

      openManualCopyDialog(
        `All imported code and document diffs (${totalDiffItems})`,
        allDiffsText,
      );
      setAllDiffsStatus("Manual copy mode opened");
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } catch (e) {
      if (allDiffsText) {
        openManualCopyDialog(
          `All imported code and document diffs (${totalDiffItems})`,
          allDiffsText,
        );
        setAllDiffsStatus("Manual copy mode opened");
      } else {
        setAllDiffsStatus(e.message || "Copy all diffs failed");
      }
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } finally {
      setAllDiffsLoading(false);
      setDocumentDiffsLoading(false);
    }
  };



  const handleRunReview = async () => {
    if (!importedWithStatus.length) {
      setReviewErr("Import modularised files first.");
      setReviewOpen(true);
      return;
    }

    if (!reviewTargetPath) {
      setReviewErr("Choose the target file being modularised.");
      setReviewOpen(true);
      return;
    }

    setReviewOpen(true);
    setReviewLoading(true);
    setReviewErr("");

    try {
      const review = await runImportZipModulariseReview({
        files: reviewableFiles,
        targetFilePath: reviewTargetPath,
        changeSummary,
        projectId: selectedProjectId || null,
      });

      setReviewResult(review || null);
    } catch (e) {
      setReviewErr(e.message);
      setReviewResult(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleSaveImportedDocument = async (doc) => {
    if (!doc) return false;
    const saveAuditAttemptId = auditAttemptRef.current;
    if (!changeSummary || !changeSummary.trim()) {
      setErr("Enter a change summary before saving.");
      return false;
    }

    setImportedDocuments((prev) =>
      (prev || []).map((item) =>
        item.path === doc.path
          ? { ...item, _status: "saving", _message: "Saving..." }
          : item,
      ),
    );

    try {
      const res = await fetch("/api/dev-ide/save-imported-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: doc.slug,
          title: doc.title,
          description: doc.description || "",
          updated_content: doc.updated_content || "",
          module_names: doc.module_names || [],
          username: currentUsername || "unknown",
          project_id: selectedProjectId || null,
          change_id: activeChange?.id || null,
          change_summary: changeSummary.trim(),
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save document.");
      }
      setImportedDocuments((prev) =>
        (prev || []).map((item) =>
          item.path === doc.path
            ? { ...item, _status: "saved", _message: "Saved document." }
            : item,
        ),
      );
      if (typeof onImportedSaved === "function") {
        onImportedSaved();
      }
      void recordSaveAudit(saveAuditAttemptId, doc.path, "documentation", true);
      markWorkflowStep(
        "changedFilesSaved",
        "success",
        `Saved document ${doc.path || doc.title || "imported document"}.`,
      );
      return true;
    } catch (e) {
      void recordSaveAudit(saveAuditAttemptId, doc.path, "documentation", false, e.message);
      markWorkflowStep(
        "changedFilesSaved",
        "failure",
        `Failed to save document ${doc.path || doc.title || "imported document"}: ${e.message}`,
      );
      setImportedDocuments((prev) =>
        (prev || []).map((item) =>
          item.path === doc.path
            ? { ...item, _status: "error", _message: e.message }
            : item,
        ),
      );
      return false;
    }
  };

  const fetchDocumentDiff = async (doc) => {
    const res = await fetch("/api/docs/documents/diff-preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        old_content: doc.original_content || "",
        new_content: doc.updated_content || "",
      }),
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || "Failed to generate document diff.");
    }
    return data.diff || null;
  };

  const handlePreviewDocumentDiff = async (doc) => {
    if (!doc) return;
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(doc.path || doc.title || "Document diff");
    setDiffCopyStatus("");
    try {
      const diff = await fetchDocumentDiff(doc);
      setDiffText((diff || {}).unified_diff || "(no diff)");
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const handlePreviewDocumentTracked = async (doc) => {
    if (!doc) return;
    setDocumentReviewOpen(true);
    setDocumentReviewLoading(true);
    setDocumentReviewErr("");
    setDocumentReviewDoc(doc);
    setDocumentReviewDiff(null);
    setDocumentReviewTab("tracked");
    try {
      const diff = await fetchDocumentDiff(doc);
      setDocumentReviewDiff(diff);
    } catch (e) {
      setDocumentReviewErr(e.message);
    } finally {
      setDocumentReviewLoading(false);
    }
  };

  const handleSaveFile = async (file) => {
    if (!file) return false;
    const saveAuditAttemptId = auditAttemptRef.current;
    if (!changeSummary || !changeSummary.trim()) {
      setErr("Enter a change summary before saving.");
      return false;
    }

    // optimistic mark row as saving
    setImported((prev) =>
      (prev || []).map((f) =>
        f.path === file.path
          ? { ...f, _status: "saving", _message: "Saving..." }
          : f,
      ),
    );

    try {
        if (isBinaryImportItem(file) && !canSaveImportItem(file)) {
        throw new Error(file.reason || "Unsupported binary asset cannot be saved.");
      }

      const body = {
        file_id: file.file_id,
        file_path: file.path,
        original_content: isBinaryImportItem(file) ? "" : file.original_content || "",
        updated_content: isBinaryImportItem(file) ? "" : file.updated_content || "",
        is_binary: Boolean(file.is_binary),
        content_base64: file.content_base64 || undefined,
        sha256: file.sha256 || undefined,
        zip_member_path: file.zip_member_path || file.path,
        change_summary: changeSummary.trim(),
        username: currentUsername || "unknown",
        change_id: activeChange && activeChange.id ? activeChange.id : null,
        project_id: selectedProjectId || null,
      };

      const res = await fetch("/api/dev-ide/save-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save file.");
      }

      const result = data.result || {};
      const msg = isBinaryImportItem(file)
        ? `Saved binary asset. Size: ${formatBytes(result.size_bytes || file.size_bytes)}; SHA: ${shortHash(result.sha256 || file.sha256)}; Backup: ${result.backup_file || "n/a"}`
        : `Saved. Backup: ${result.backup_file || "n/a"}`;
      setImported((prev) =>
        (prev || []).map((f) =>
          f.path === file.path ? { ...f, _status: "saved", _message: msg } : f,
        ),
      );

      if (typeof onImportedSaved === "function") {
        onImportedSaved();
      }
      void recordSaveAudit(saveAuditAttemptId, file.path, isBinaryImportItem(file) ? "binary" : "code", true);
      markWorkflowStep(
        "changedFilesSaved",
        "success",
        `Saved file ${file.path || "imported file"}.`,
      );
      return true;
    } catch (e) {
      void recordSaveAudit(saveAuditAttemptId, file.path, isBinaryImportItem(file) ? "binary" : "code", false, e.message);
      markWorkflowStep(
        "changedFilesSaved",
        "failure",
        `Failed to save file ${file.path || "imported file"}: ${e.message}`,
      );
      setImported((prev) =>
        (prev || []).map((f) =>
          f.path === file.path
            ? { ...f, _status: "error", _message: e.message }
            : f,
        ),
      );
      return false;
    }
  };

  const isUnconfiguredPostImportAction = (actionKey, message = "") => {
    const lowerMessage = String(message || "").toLowerCase();
    const action = POST_IMPORT_ACTIONS[actionKey] || {};
    return (action.unconfiguredMessages || []).some((needle) =>
      lowerMessage.includes(String(needle || "").toLowerCase()),
    );
  };

  const runPostImportAction = async (actionKey, { stopOnFailure = false } = {}) => {
    const action = POST_IMPORT_ACTIONS[actionKey];
    if (!action) return { passed: false, skipped: false, message: "Unknown action." };

    setPostImportActions((prev) => ({
      ...prev,
      [actionKey]: { status: "Running", output: "", error: "" },
    }));
    if (action.stepKey) {
      markWorkflowStep(action.stepKey, "running", action.runningMessage || `${action.label} started from Import Review modal.`);
    }

    try {
      const res = await fetch(action.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: selectedProjectId || null,
        }),
      });
      const data = await res.json().catch(() => null);
      const passed = Boolean(res.ok && data && data.success);
      const summary = data
        ? summariseCommandResult(data)
        : `Request failed with HTTP ${res.status}.`;

      const failureMessage = data?.error || data?.error_output || `HTTP ${res.status}`;
      const skipped = !passed && isUnconfiguredPostImportAction(actionKey, failureMessage);
      setPostImportActions((prev) => ({
        ...prev,
        [actionKey]: {
          status: passed ? "Passed" : skipped ? "Skipped" : "Failed",
          output: skipped ? `${action.label} skipped because it is not configured.

${summary}` : summary,
          error: passed || skipped ? "" : failureMessage,
        },
      }));
      if (action.stepKey) {
        markWorkflowStep(
          action.stepKey,
          passed ? "success" : skipped ? "pending" : "failure",
          passed
            ? action.successMessage || `${action.label} completed.`
            : skipped
              ? action.skippedMessage || `${action.label} skipped because no command is configured.`
              : `${action.failurePrefix || `${action.label} failed`}: ${failureMessage}`,
        );
      }

      if (!passed && !skipped && stopOnFailure) {
        throw new Error(`${action.label} failed: ${failureMessage}`);
      }
      return { passed, skipped, message: failureMessage };
    } catch (e) {
      const errorMessage = e.message || String(e);
      setPostImportActions((prev) => ({
        ...prev,
        [actionKey]: {
          status: "Failed",
          output: "",
          error: errorMessage,
        },
      }));
      if (action.stepKey) {
        markWorkflowStep(action.stepKey, "failure", `${action.failurePrefix || `${action.label} failed`}: ${errorMessage}`);
      }
      if (stopOnFailure) throw e;
      return { passed: false, skipped: false, message: errorMessage };
    }
  };

  const handleRunPostImportAction = async (actionKey) => {
    await runPostImportAction(actionKey);
  };

  const saveAllChangedFiles = async () => {
    saveAllLastErrorRef.current = "";
    const readyFiles = importedWithStatus.filter(
      (file) =>
        file._status === "ready" &&
        file.change_kind !== "unchanged" &&
        canSaveImportItem(file),
    );
    const readyDocs = importedDocumentsWithStatus.filter(
      (doc) => doc._status === "ready" && doc.change_kind !== "unchanged",
    );

    if (!readyFiles.length && !readyDocs.length && !createableSkippedFiles.length) {
      if (importSummary.savedCount) return true;
      saveAllLastErrorRef.current = "No ready changed files or documents to save.";
      setErr(saveAllLastErrorRef.current);
      return false;
    }
    if (!changeSummary || !changeSummary.trim()) {
      saveAllLastErrorRef.current = "Enter a change summary before saving.";
      setErr(saveAllLastErrorRef.current);
      return false;
    }
    if (saveAutomationBlockedByRiskGate) {
      saveAllLastErrorRef.current =
        "Import review risk gate blocked Save All automation. Use manual row-level review/save only after resolving the blocked findings.";
      setErr(saveAllLastErrorRef.current);
      return false;
    }

    setErr("");

    if (createableSkippedFiles.length) {
      const created = await createMissingImportFiles(createableSkippedFiles, {
        preserveSaveAutomationState: true,
        successPrefix: "Save All created",
      });
      if (!created) {
        saveAllLastErrorRef.current = "Save All stopped while creating new files.";
        setErr(saveAllLastErrorRef.current);
        return false;
      }
    }

    for (const file of readyFiles) {
      // Reuse the same save route and status updates used by each file row.
      const saved = await handleSaveFile(file);
      if (!saved) {
        saveAllLastErrorRef.current = `Save All stopped at failed code file: ${file.path || "imported file"}.`;
        setErr(saveAllLastErrorRef.current);
        return false;
      }
    }
    for (const doc of readyDocs) {
      // Reuse the same save route and status updates used by each document row.
      const saved = await handleSaveImportedDocument(doc);
      if (!saved) {
        saveAllLastErrorRef.current = `Save All stopped at failed document: ${doc.path || doc.title || "imported document"}.`;
        setErr(saveAllLastErrorRef.current);
        return false;
      }
    }
    return true;
  };

  const handleSaveAllChangedFiles = async () => {
    setSaveAllLoading(true);
    setSaveAndVerifyStatus({ status: "Not run", message: "" });
    try {
      await saveAllChangedFiles();
    } finally {
      setSaveAllLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !shouldAutoSaveAfterImport || autoSaveAfterImportAttemptedRef.current) return;
    if (loading || saveAllLoading || saveAndVerifyLoading) return;
    if (!importedWithStatus.length && !importedDocumentsWithStatus.length && !skipped.length && !skippedDocuments.length) return;

    if (!readyChangedImportCount) {
      autoSaveAfterImportAttemptedRef.current = true;
      setAutoSaveAfterImportStatus({
        status: "skipped",
        message: "Auto-save skipped: no changed files.",
      });
      return;
    }

    if (saveAutomationBlockedByRiskGate) {
      autoSaveAfterImportAttemptedRef.current = true;
      const message = "Auto-save failed: Import Review risk gate blocked Save All automation.";
      setAutoSaveAfterImportStatus({ status: "failed", message });
      markWorkflowStep("changedFilesSaved", "failure", message);
      return;
    }

    autoSaveAfterImportAttemptedRef.current = true;
    const runAutoSaveAfterImport = async () => {
      setSaveAllLoading(true);
      setAutoSaveAfterImportStatus({
        status: "running",
        message: "Auto-saving changed files from returned package...",
      });
      try {
        const saved = await saveAllChangedFiles();
        if (saved) {
          setAutoSaveAfterImportStatus({
            status: "saved",
            message: "Auto-save complete.",
          });
          markWorkflowStep("changedFilesSaved", "success", "Auto-save complete for returned package.");
        } else {
          const message = saveAllLastErrorRef.current || "Auto-save failed during Save All Changed Files.";
          setAutoSaveAfterImportStatus({
            status: "failed",
            message: message.startsWith("Save All stopped at failed") ? message.replace("Save All stopped at failed", "Auto-save failed at") : message,
          });
          markWorkflowStep("changedFilesSaved", "failure", message);
        }
      } catch (e) {
        const message = e.message || "Auto-save failed during Save All Changed Files.";
        setAutoSaveAfterImportStatus({ status: "failed", message });
        markWorkflowStep("changedFilesSaved", "failure", message);
      } finally {
        setSaveAllLoading(false);
      }
    };

    runAutoSaveAfterImport();
    // Intentionally constrained to returned import state flags; save helpers are stable enough for this controlled one-shot automation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    shouldAutoSaveAfterImport,
    loading,
    saveAllLoading,
    saveAndVerifyLoading,
    readyChangedImportCount,
    saveAutomationBlockedByRiskGate,
    importedWithStatus,
    importedDocumentsWithStatus,
    skipped,
    skippedDocuments,
  ]);

  useEffect(() => {
    if (!open || !controlledSaveEnabled || controlledSaveAttemptedRef.current) return;
    if (loading || saveAllLoading || saveAndVerifyLoading) return;
    if (!riskGate || !readyChangedImportCount) return;

    if (riskGate.decision === "block_automation") {
      controlledSaveAttemptedRef.current = true;
      setControlledSaveStatus({
        status: "blocked",
        message: "Controlled Codex save paused because the import risk gate blocked automation.",
      });
      markWorkflowStep("changedFilesSaved", "failure", "Controlled Codex save blocked by Import Review risk gate.");
      return;
    }

    if (riskGate.decision === "require_human_review" && !humanApprovedRiskGate) {
      setControlledSaveStatus({
        status: "running",
        message: "Controlled Codex save is proceeding after medium-risk review findings. Findings remain visible for post-save human review; no build or restart will run.",
      });
    }

    controlledSaveAttemptedRef.current = true;
    const runControlledSave = async () => {
      setSaveAllLoading(true);
      setControlledSaveStatus({
        status: "running",
        message: "Controlled Codex save is saving non-blocked changed files through the existing Import Review Save All path. Medium-risk findings remain visible; no build or restart will run.",
      });
      try {
        const saved = await saveAllChangedFiles();
        if (saved) {
          setControlledSaveStatus({
            status: "saved",
            message: "Controlled Codex save completed. Changed files were saved for the next linked DEV IDE export. No build, restart, health check, or verification was run.",
          });
        } else {
          setControlledSaveStatus({
            status: "failed",
            message: "Controlled Codex save paused because Save All Changed Files did not complete. Review the Import Review error before continuing.",
          });
        }
      } catch (e) {
        setControlledSaveStatus({
          status: "failed",
          message: e.message || "Controlled Codex save failed during Import Review Save All.",
        });
      } finally {
        setSaveAllLoading(false);
      }
    };

    runControlledSave();
    // Intentionally constrained to controlled-save trigger state to prevent repeated save attempts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    controlledSaveEnabled,
    loading,
    saveAllLoading,
    saveAndVerifyLoading,
    riskGate,
    readyChangedImportCount,
    humanApprovedRiskGate,
  ]);

  const handleSaveAllAndVerify = async () => {
    if (
      highRiskDependencyFindings.length &&
      !dependencyFindingsAcknowledged
    ) {
      setSaveAndVerifyStatus({
        status: "Failed",
        message:
          "Review and acknowledge high-severity dependency findings before running Save All and Verify.",
      });
      setErr(
        "Dependency findings require acknowledgement before Save All and Verify. Add or review the required manifest updates first.",
      );
      return;
    }

    setSaveAndVerifyLoading(true);
    setSaveAndVerifyStatus({ status: "Running", message: "Saving changed files..." });
    try {
      const saved = await saveAllChangedFiles();
      if (!saved) {
        setSaveAndVerifyStatus({
          status: "Failed",
          message: "Stopped during Save All Changed Files. Review the failed row or error message above.",
        });
        return;
      }

      for (const actionKey of Object.keys(POST_IMPORT_ACTIONS)) {
        const action = POST_IMPORT_ACTIONS[actionKey];
        setSaveAndVerifyStatus({
          status: "Running",
          message: `${action.label} is running...`,
        });
        await runPostImportAction(actionKey, { stopOnFailure: true });
      }

      setSaveAndVerifyStatus({
        status: "Passed",
        message: "Save All and Verify completed. Unconfigured actions are marked as skipped.",
      });
      markWorkflowStep(
        "verificationSummaryCopied",
        "success",
        "Save All and Verify completed in the Import Review modal.",
      );
    } catch (e) {
      setSaveAndVerifyStatus({
        status: "Failed",
        message: e.message || String(e),
      });
    } finally {
      setSaveAndVerifyLoading(false);
    }
  };

  return (
    <Dialog data-testid="import-zip-dialog" open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {includeDocuments ? "Import ZIP with docs" : "Import files from ZIP"}
      </DialogTitle>
      <DialogContent dividers sx={{ pb: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload a ZIP that contains repo-relative paths (e.g.{" "}
            <code>backend/...</code> or <code>src/...</code>). Files must exist
            in the Dev IDE file map to be importable.{" "}
            {includeDocuments
              ? "Markdown documents under docs/ will also be previewed and can be saved below."
              : ""}
          </Typography>

          {err ? <Alert data-testid="import-zip-error" severity="error">{err}</Alert> : null}
          {auditState.state !== "idle" ? (
            <Alert data-testid="import-zip-audit-state" severity={auditState.state === "recorded" ? "info" : "warning"}>
              {auditState.state === "recorded"
                ? `Import preview audit recorded for attempt #${auditAttemptRef.current}. Review is not evidence of saving.`
                : `Audit ${auditState.state}: ${auditState.message || "Not all import/save events are durably recorded."}`}
            </Alert>
          ) : null}
          {createInfo ? (
            <Alert
              severity={
                createInfo.toLowerCase().includes("create failed")
                  ? "error"
                  : "info"
              }
            >
              {createInfo}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">1. Choose a returned ZIP (recommended)</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  data-testid="import-zip-choose"
                  variant="outlined"
                  component="label"
                  disabled={loading || saveAllLoading || saveAndVerifyLoading || controlledSaveStatus.status === "running" || autoSaveAfterImportStatus.status === "running"}
                >
                  Choose ZIP
                  <input
                    data-testid="import-zip-file-input"
                    hidden
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    onChange={handlePickedZip}
                  />
                </Button>
                <Typography data-testid="import-zip-selected-filename" variant="body2" sx={{ alignSelf: "center", overflowWrap: "anywhere" }}>
                  {zipFile ? zipFile.name : "No file selected"}
                  {zipFile ? ` (${formatBytes(zipFile.size)})` : ""}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  data-testid="import-zip-import"
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!zipFile || Boolean(zipFileError) || loading || saveAllLoading || saveAndVerifyLoading}
                >
                  {loading ? "Validating…" : "Validate & Review"}
                </Button>
              </Stack>
              {zipFile ? (
                <Alert data-testid="import-zip-file-validation" severity={zipFileError || zipFileValidation === "rejected" ? "error" : zipFileValidation === "validated" ? "success" : "info"}>
                  {zipFileError || (zipFileValidation === "rejected"
                    ? "The package was rejected by the importer. See the error above; you can choose another ZIP or retry."
                    : zipFileValidation === "validated"
                      ? "Server validation completed. Review imported items below before saving."
                      : "Filename and size checks passed. ZIP contents, paths, and manifest are not yet validated; click Validate & Review.")}
                </Alert>
              ) : null}

              <Divider />
              <Typography variant="subtitle2" color="text.secondary">2. Alternate: Import from server path (including Codex handoffs)</Typography>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  inputProps={{ "data-testid": "import-zip-path-input" }}
                  label="Import from path"
                  placeholder="/root/Downloads/completed-change-package.zip"
                  value={zipPath}
                  onChange={(e) => setZipPath(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && zipPath.trim() && !loading) {
                      e.preventDefault();
                      handleImportFromPath();
                    }
                  }}
                  size="small"
                  fullWidth
                  helperText={
                    codexHandoffContext
                      ? "Codex returned ZIP detected from the configured incoming handoff folder. Import opens review only and does not save, rebuild, restart, or progress to the next change."
                      : "For browser agents: paste a backend-accessible absolute ZIP path, then press Enter or click Import from Path."
                  }
                />
                <Button
                  data-testid="import-zip-import-from-path"
                  variant="outlined"
                  onClick={handleImportFromPath}
                  disabled={!zipPath.trim() || loading}
                  sx={{ minWidth: 180 }}
                >
                  {codexHandoffContext ? "Load Codex Return" : "Import from Path"}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <TextField
            label="Change summary (used for Save)"
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            size="small"
            fullWidth
          />

          {importedWithStatus.length > 0 ? (
            <>
              {hasSkippedFiles ? (
                <Alert severity="info">
                  Verify modularisation will include skipped new files using
                  their full ZIP content, even before they are created. Create
                  them before save if the review passes.
                </Alert>
              ) : null}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  select
                  label="Target file being modularised"
                  value={reviewTargetPath}
                  onChange={(e) => setReviewTargetPath(e.target.value)}
                  size="small"
                  sx={{ minWidth: { md: 420 } }}
                >
                  {filteredImportedFiles.map((f) => (
                    <MenuItem key={f.path} value={f.path}>
                      {f.path}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  onClick={handleRunReview}
                  disabled={loading || reviewLoading || !reviewTargetPath}
                >
                  Verify modularisation
                </Button>
              </Stack>
            </>
          ) : null}

          {loading ? <LinearProgress /> : null}

          {importedWithStatus.length > 0 ||
          importedDocumentsWithStatus.length > 0 ||
          sqlArtifactsWithPreview.length > 0 ||
          hasSkippedFiles ||
          hasSkippedDocuments ? (
            <>
              <Divider />

              <Paper
                variant="outlined"
                sx={{ p: 1.5, backgroundColor: "background.default" }}
              >
                <Stack spacing={1.25}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        Agent Import Review Summary
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Review the imported package before saving individual
                        files or documents. SQL artifacts are review-only and are not executed automatically.
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handlePreviewAllDiffs}
                      disabled={
                        !importedWithStatus.length || loading || allDiffsLoading
                      }
                    >
                      Review all diffs
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCopyAllDiffs}
                      disabled={
                        (!importedWithStatus.length &&
                          !getChangedImportedDocuments().length) ||
                        loading ||
                        allDiffsLoading ||
                        documentDiffsLoading
                      }
                    >
                      {allDiffsLoading || documentDiffsLoading
                        ? "Copying..."
                        : "Copy all diffs"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCopyDiffReviewPrompt}
                      disabled={
                        !importedWithStatus.length || loading || allDiffsLoading
                      }
                    >
                      {allDiffsLoading
                        ? "Copying..."
                        : "Copy GPT review prompt"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handlePreviewAllDocumentDiffs}
                      disabled={
                        !getChangedImportedDocuments().length ||
                        loading ||
                        documentDiffsLoading
                      }
                    >
                      Review doc diffs
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleCopyAgentHandoffSummary(false)}
                      disabled={loading || allDiffsLoading || documentDiffsLoading}
                    >
                      Copy Agent Summary
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleCopyAgentHandoffSummary(true)}
                      disabled={loading || allDiffsLoading || documentDiffsLoading}
                    >
                      {allDiffsLoading || documentDiffsLoading
                        ? "Copying..."
                        : "Copy Summary with Diffs"}
                    </Button>
                  </Stack>

                  {agentHandoffStatus ? (
                    <Alert severity={agentHandoffStatus.includes("failed") ? "error" : "success"}>
                      {agentHandoffStatus}
                    </Alert>
                  ) : null}

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={`Changed code: ${importSummary.changedCode}`}
                      color="warning"
                      variant={
                        importSummary.changedCode ? "filled" : "outlined"
                      }
                    />
                    <Chip
                      size="small"
                      label={`New code: ${importSummary.newCode}`}
                      color="info"
                      variant={importSummary.newCode ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label={`Binary assets: ${importSummary.totalBinaryFiles}`}
                      color="info"
                      variant={importSummary.totalBinaryFiles ? "filled" : "outlined"}
                    />
                    {importSummary.unsupportedBinaryFiles ? (
                      <Chip
                        size="small"
                        label={`Unsupported binary: ${importSummary.unsupportedBinaryFiles}`}
                        color="error"
                        variant="filled"
                      />
                    ) : null}
                    {importSummary.hashMismatchCount ? (
                      <Chip
                        size="small"
                        label={`Hash mismatches: ${importSummary.hashMismatchCount}`}
                        color="error"
                        variant="filled"
                      />
                    ) : null}
                    <Chip
                      size="small"
                      label={`Changed docs: ${importSummary.changedDocs}`}
                      color="warning"
                      variant={
                        importSummary.changedDocs ? "filled" : "outlined"
                      }
                    />
                    <Chip
                      size="small"
                      label={`New docs: ${importSummary.newDocs}`}
                      color="info"
                      variant={importSummary.newDocs ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label={`Ready to save: ${importSummary.readyToSave}`}
                      color="primary"
                      variant={
                        importSummary.readyToSave ? "filled" : "outlined"
                      }
                    />
                    <Chip
                      size="small"
                      label={`Saved: ${importSummary.savedCount}`}
                      color="success"
                      variant={importSummary.savedCount ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label={`Saved text: ${importSummary.savedTextFileCount + importSummary.savedDocumentCount}`}
                      color="success"
                      variant={importSummary.savedTextFileCount + importSummary.savedDocumentCount ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label={`Saved binary: ${importSummary.savedBinaryCount}`}
                      color="success"
                      variant={importSummary.savedBinaryCount ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      label={`Skipped: ${importSummary.skippedCode + importSummary.skippedDocs}`}
                      color={
                        importSummary.skippedCode + importSummary.skippedDocs
                          ? "warning"
                          : "default"
                      }
                      variant={
                        importSummary.skippedCode + importSummary.skippedDocs
                          ? "filled"
                          : "outlined"
                      }
                    />
                    {importSummary.totalSqlArtifacts ? (
                      <Chip
                        size="small"
                        label={`SQL artifacts: ${importSummary.totalSqlArtifacts}`}
                        color="warning"
                        variant="filled"
                      />
                    ) : null}
                    {importSummary.environmentCapabilityFindings ? (
                      <Chip
                        size="small"
                        label={`Browser capability checks: ${importSummary.environmentCapabilityFindings}`}
                        color="warning"
                        variant="filled"
                      />
                    ) : null}
                    {zipStandard?.standard_version ? (
                      <Chip
                        size="small"
                        label={`Doc standard: ${zipStandard.standard_version}`}
                      />
                    ) : null}
                  </Stack>

                  {riskGate ? (
                    <Alert severity={getRiskGateSeverity(riskGate)}>
                      <Stack spacing={0.75}>
                        <Typography variant="subtitle2">
                          Import Review Risk Gate: {String(riskGate.risk_level || "unknown").toUpperCase()} — {getRiskGateDecisionLabel(riskGate)}
                        </Typography>
                        <Typography variant="body2">
                          {riskGate.summary || "Risk gate completed for this import preview."}
                        </Typography>
                        {riskGate.metrics ? (
                          <Typography variant="caption" color="text.secondary">
                            Diff lines: {riskGate.metrics.total_diff_lines || 0}; deleted lines: {riskGate.metrics.total_deleted_lines || 0}; skipped items: {(riskGate.metrics.skipped_code || 0) + (riskGate.metrics.skipped_docs || 0)}; high-risk files: {riskGate.metrics.high_risk_files || 0}
                          </Typography>
                        ) : null}
                        {(riskGate.findings || []).length ? (
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {(riskGate.findings || []).slice(0, 6).map((finding, index) => (
                              <Typography key={`risk-finding-${index}`} component="li" variant="caption">
                                {finding.path ? `${finding.path}: ` : ""}{finding.message || finding.code}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                      </Stack>
                    </Alert>
                  ) : null}

                  {riskGate && riskGate.decision === "require_human_review" ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={humanApprovedRiskGate}
                          onChange={(event) => setHumanApprovedRiskGate(event.target.checked)}
                        />
                      }
                      label="I have reviewed the risk-gate findings and approve controlled Save All for this imported package."
                    />
                  ) : null}

                  {(dependencyFindings || []).length || (environmentCapabilityFindings || []).length ? (
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: highRiskDependencyFindings.length
                          ? "warning.main"
                          : "divider",
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="subtitle2">
                              Dependency & Environment Checks
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Saving code is separate from completing dependency
                              manifests, install/build verification, and browser
                              capability checks.
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={copyDependencyFixSummary}
                          >
                            Copy fix summary
                          </Button>
                        </Stack>

                        <Alert
                          severity={
                            highRiskDependencyFindings.length
                              ? "warning"
                              : "info"
                          }
                        >
                          {highRiskDependencyFindings.length
                            ? `${highRiskDependencyFindings.length} high-severity dependency finding(s) need manifest review before Save All and Verify.`
                            : (dependencyFindings || []).length
                              ? "Dependency findings are present for operator review."
                              : "Browser environment capability findings are present for operator review."}{" "}
                          Camera/getUserMedia and service workers require HTTPS
                          or localhost; clipboard, geolocation, and
                          notifications may also require secure context,
                          permissions, or device capability checks.
                        </Alert>

                        {(dependencyFindings || []).length ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Package</TableCell>
                                <TableCell>Ecosystem</TableCell>
                                <TableCell>Source file</TableCell>
                                <TableCell>Severity</TableCell>
                                <TableCell>Required action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(dependencyFindings || []).map((finding, index) => (
                                <TableRow key={`dependency-finding-${index}`}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={700}>
                                      {finding.package_name || "Unknown"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{finding.ecosystem || "unknown"}</TableCell>
                                  <TableCell sx={{ wordBreak: "break-word" }}>
                                    {finding.source_file_path || "unknown"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={finding.severity || "review"}
                                      color={
                                        String(finding.severity || "").toLowerCase() ===
                                        "high"
                                          ? "warning"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell sx={{ wordBreak: "break-word" }}>
                                    {finding.required_manifest_action ||
                                      finding.message ||
                                      "Review dependency manifest update."}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : null}

                        {(environmentCapabilityFindings || []).length ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Capability</TableCell>
                                <TableCell>Source file</TableCell>
                                <TableCell>Severity</TableCell>
                                <TableCell>Environment requirement</TableCell>
                                <TableCell>Manual smoke-test guidance</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(environmentCapabilityFindings || []).map((finding, index) => (
                                <TableRow key={`environment-capability-${index}`}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={700}>
                                      {finding.label || finding.capability || "Browser API"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ wordBreak: "break-word" }}>
                                    {finding.source_file_path || "unknown"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={finding.severity || "review"}
                                      color={
                                        String(finding.severity || "").toLowerCase() ===
                                        "high"
                                          ? "warning"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell sx={{ wordBreak: "break-word" }}>
                                    {finding.environment_requirement ||
                                      finding.message ||
                                      "Review secure-context and browser permission requirements."}
                                  </TableCell>
                                  <TableCell sx={{ wordBreak: "break-word" }}>
                                    {finding.manual_smoke_test ||
                                      "Record pass/fail/notes in the Full Change smoke-test checklist."}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : null}

                        {highRiskDependencyFindings.length ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={dependencyFindingsAcknowledged}
                                onChange={(event) =>
                                  setDependencyFindingsAcknowledged(
                                    event.target.checked,
                                  )
                                }
                              />
                            }
                            label="I have reviewed the high-severity dependency findings and understand Save All and Verify is not complete until manifests, installs, and verification pass."
                          />
                        ) : null}

                        {dependencyCopyStatus ? (
                          <Typography variant="caption" color="text.secondary">
                            {dependencyCopyStatus}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>
                  ) : null}

                  {controlledSaveEnabled ? (
                    <Alert data-testid="import-zip-controlled-save-status" severity={controlledSaveStatus.status === "failed" || controlledSaveStatus.status === "blocked" ? "error" : controlledSaveStatus.status === "saved" ? "success" : "info"}>
                      Controlled Codex save is enabled for this Full Change. {controlledSaveStatus.message || "It will use Save All Changed Files unless the risk gate explicitly blocks automation. Medium-risk review findings remain visible but do not stop file save. It will not run build, restart, health check, or verification."}
                    </Alert>
                  ) : null}

                  {shouldAutoSaveAfterImport ? (
                    <Alert data-testid="import-zip-auto-save-status" severity={autoSaveAfterImportStatus.status === "failed" ? "error" : autoSaveAfterImportStatus.status === "saved" ? "success" : autoSaveAfterImportStatus.status === "skipped" ? "warning" : "info"}>
                      Returned-package auto-save is enabled. {autoSaveAfterImportStatus.message || "After a successful import with changed files, DEV IDE will run the existing Save All Changed Files process only. It will not run build, restart, health check, verification, or next-change progression."}
                    </Alert>
                  ) : null}

                  {agentImportReview.hasNoChangedFiles ? (
                    <Alert severity="warning">
                      No changed files were detected in this import. Review the
                      package contents before saving.
                    </Alert>
                  ) : null}

                  {agentImportReview.outsideStructurePaths.length ? (
                    <Alert severity="warning">
                      {agentImportReview.outsideStructurePaths.length} imported
                      path(s) appear outside the expected project/module
                      structure: {EXPECTED_IMPORT_PATH_PREFIXES.join(", ")}
                    </Alert>
                  ) : null}

                  <Tabs
                    value={importFilter}
                    onChange={(_, value) => setImportFilter(value)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                  >
                    <Tab value="all" label="All" />
                    <Tab value="code" label="Code" />
                    <Tab value="docs" label="Docs" />
                    <Tab value="changed_only" label="Changed only" />
                    <Tab value="changed_docs_only" label="Changed docs only" />
                    <Tab value="new" label="New only" />
                    <Tab value="ready" label="Ready" />
                    <Tab value="saved" label="Saved" />
                  </Tabs>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                      >
                        Package contents
                      </Typography>
                      <Typography variant="body2">
                        {importSummary.totalCode} code file(s),{" "}
                        {importSummary.totalDocs} document(s),{" "}
                        {importSummary.totalSqlArtifacts} SQL artifact(s),{" "}
                        {groupedReview.length} grouped review bucket(s).
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                      >
                        Apply readiness
                      </Typography>
                      <Typography variant="body2">
                        {importSummary.readyToSave} item(s) are still ready for
                        save.{" "}
                        {importSummary.errorCount
                          ? `${importSummary.errorCount} item(s) have save errors.`
                          : "No save errors detected yet."}
                      </Typography>
                    </Paper>
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                        >
                          Review scenario coverage
                        </Typography>
                        <Typography variant="body2">
                          {reviewCoverageHint}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {reviewCoverage.map((item) => (
                          <Chip
                            key={item.key}
                            size="small"
                            label={`${item.label}: ${item.count}`}
                            color={item.present ? item.color : "default"}
                            variant={item.present ? "filled" : "outlined"}
                          />
                        ))}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {reviewCoverage.map((item) => (
                          <Button
                            key={`${item.key}-filter`}
                            size="small"
                            variant={
                              importFilter === item.filterValue
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() => setImportFilter(item.filterValue)}
                            disabled={
                              !item.present && item.filterValue !== "all"
                            }
                          >
                            {item.filterLabel}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>

              {allDiffsStatus ? (
                <Alert
                  severity={
                    allDiffsStatus.toLowerCase().includes("failed")
                      ? "error"
                      : "success"
                  }
                >
                  {allDiffsStatus}
                </Alert>
              ) : null}

              {documentDiffsStatus ? (
                <Alert
                  severity={
                    documentDiffsStatus.toLowerCase().includes("failed")
                      ? "error"
                      : "success"
                  }
                >
                  {documentDiffsStatus}
                </Alert>
              ) : null}

              {documentComplianceSummary?.modules?.length ? (
                <Alert
                  severity={
                    importSummary.missingDocModules ? "warning" : "success"
                  }
                >
                  {importSummary.missingDocModules
                    ? `Document standards check: ${importSummary.missingDocModules} module scope(s) still have missing standard docs in this import package.`
                    : "Document standards check: imported module docs cover the current standard set."}
                </Alert>
              ) : null}

              {documentComplianceSummary?.modules?.length ? (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Document standards review
                  </Typography>
                  <Stack spacing={1}>
                    {documentComplianceSummary.modules
                      .slice(0, 8)
                      .map((entry) => (
                        <Box key={entry.module_name}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: "block" }}
                          >
                            {entry.module_name}
                          </Typography>
                          {(entry.missing_labels || []).length ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Missing: {entry.missing_labels.join(", ")}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="success.main">
                              Standard document set covered in import package.
                            </Typography>
                          )}
                        </Box>
                      ))}
                  </Stack>
                </Paper>
              ) : null}

              {importedWithStatus.length > 0 ? (
                <>
                  {hasSkippedFiles ? (
                    <Alert severity="info">
                      Verify modularisation will include skipped new files using
                      their full ZIP content, even before they are created.
                      Create them before save if the review passes.
                    </Alert>
                  ) : null}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField
                      select
                      label="Target file being modularised"
                      value={reviewTargetPath}
                      onChange={(e) => setReviewTargetPath(e.target.value)}
                      size="small"
                      sx={{ minWidth: { md: 420 } }}
                    >
                      {filteredImportedFiles.map((f) => (
                        <MenuItem key={f.path} value={f.path}>
                          {f.path}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      onClick={handleRunReview}
                      disabled={loading || reviewLoading || !reviewTargetPath}
                    >
                      Verify modularisation
                    </Button>
                  </Stack>
                </>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Grouped review
                </Typography>
                <Stack spacing={1}>
                  {groupedReview.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No import items match the current filter.
                    </Typography>
                  ) : (
                    groupedReview.map((group) => (
                      <Paper
                        key={group.key}
                        variant="outlined"
                        sx={{ p: 1.25 }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", md: "center" }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {group.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {group.items.length} review item(s) in this group.
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              size="small"
                              label={`Changed ${group.changedCount}`}
                              color="warning"
                              variant={
                                group.changedCount ? "filled" : "outlined"
                              }
                            />
                            <Chip
                              size="small"
                              label={`New ${group.newCount}`}
                              color="info"
                              variant={group.newCount ? "filled" : "outlined"}
                            />
                            <Chip
                              size="small"
                              label={`Unchanged ${group.unchangedCount}`}
                              variant="outlined"
                            />
                            {group.skippedCount ? (
                              <Chip
                                size="small"
                                label={`Skipped ${group.skippedCount}`}
                                color="warning"
                                variant="filled"
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Paper>

              {sqlArtifactsWithPreview.length ? (
                <Paper variant="outlined" sx={{ p: 2, borderColor: "warning.light" }}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle2">
                        SQL Artifacts ({sqlArtifactsWithPreview.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Imported SQL is stored for human review only. Import Review does not execute SQL automatically. Review and execute manually, or use a future explicit SQL execution action after approval.
                      </Typography>
                    </Box>
                    {sqlArtifactCopyStatus ? (
                      <Alert
                        severity={
                          sqlArtifactCopyStatus === "Manual copy mode opened"
                            ? "info"
                            : "success"
                        }
                      >
                        {sqlArtifactCopyStatus}
                      </Alert>
                    ) : null}
                    <Paper variant="outlined" sx={{ overflow: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>SQL file</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Linked change</TableCell>
                            <TableCell>Review status</TableCell>
                            <TableCell>Execution status</TableCell>
                            <TableCell>Safe preview</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sqlArtifactsWithPreview.map((artifact, index) => (
                            <TableRow key={artifact.id || artifact._path || index}>
                              <TableCell
                                sx={{
                                  maxWidth: 360,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={artifact._path}
                              >
                                {artifact._path}
                              </TableCell>
                              <TableCell>
                                {artifact.sql_type || artifact.artifact_type || "sql"}
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.25}>
                                  <Typography variant="caption">
                                    Full Change: {artifact.full_change_id || "—"}
                                  </Typography>
                                  <Typography variant="caption">
                                    DEV IDE Change: {artifact.dev_ide_change_id || "—"}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={artifact.review_status || "pending_review"}
                                  color="warning"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={artifact.execution_status || "not_executed"}
                                  color={
                                    artifact.execution_status === "executed"
                                      ? "success"
                                      : "default"
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 320, maxWidth: 520 }}>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    maxHeight: 110,
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontSize: 12,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {truncateSqlPreview(
                                    artifact._preview ||
                                      "-- SQL content preview was not returned by the backend.",
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="flex-end"
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleViewSqlArtifact(artifact)}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCopySqlArtifact(artifact)}
                                  >
                                    Copy
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Stack>
                </Paper>
              ) : null}

              {importFilter !== "docs" ? (
                <>
                  <Typography variant="subtitle2">
                    Code review ({filteredImportedFiles.length}/
                    {importedWithStatus.length})
                  </Typography>
                  {isMobile ? (
                    <MobileReviewCardList
                      items={filteredImportedFiles}
                      type="code"
                      onPreviewDiff={handlePreviewDiff}
                      onSave={handleSaveFile}
                      loading={loading}
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ overflow: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Path</TableCell>
                            <TableCell>Module</TableCell>
                            <TableCell>Change</TableCell>
                            <TableCell>Binary details</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredImportedFiles.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No imported files in this view.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredImportedFiles.map((f) => (
                              <TableRow key={f.path}>
                                <TableCell
                                  sx={{
                                    maxWidth: 520,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {f.path}
                                </TableCell>
                                <TableCell>{f.module || ""}</TableCell>
                                <TableCell>{f.change_kind}</TableCell>
                                <TableCell>
                                  <BinaryAssetDetails file={f} />
                                  {isBinaryImportItem(f) ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Text diff disabled for binary assets. Verify size/hash before saving.
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">
                                      {toReviewStatusLabel(f._status)}
                                    </Typography>
                                    {f._message ? (
                                      <Typography
                                        variant="caption"
                                        color={
                                          f._status === "error"
                                            ? "error"
                                            : "text.secondary"
                                        }
                                      >
                                        {f._message}
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    {!isBinaryImportItem(f) ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handlePreviewDiff(f)}
                                        disabled={loading}
                                      >
                                        Preview diff
                                      </Button>
                                    ) : null}
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleSaveFile(f)}
                                      disabled={
                                        f._status === "saved" ||
                                        f._status === "saving" ||
                                        loading ||
                                        !canSaveImportItem(f)
                                      }
                                    >
                                      Save
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </>
              ) : null}

              {includeDocuments && importFilter !== "code" ? (
                <>
                  <Typography variant="subtitle2">
                    Document review ({filteredImportedDocuments.length}/
                    {importedDocumentsWithStatus.length})
                  </Typography>
                  {isMobile ? (
                    <MobileReviewCardList
                      items={filteredImportedDocuments}
                      type="docs"
                      onPreviewDiff={handlePreviewDocumentDiff}
                      onTrackedPreview={handlePreviewDocumentTracked}
                      onSave={handleSaveImportedDocument}
                      loading={loading}
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ overflow: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Path</TableCell>
                            <TableCell>Modules</TableCell>
                            <TableCell>Change</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredImportedDocuments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No imported documents in this view.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredImportedDocuments.map((doc) => (
                              <TableRow key={doc.path}>
                                <TableCell
                                  sx={{
                                    maxWidth: 420,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {doc.path}
                                </TableCell>
                                <TableCell>
                                  {(doc.module_names || []).join(", ")}
                                </TableCell>
                                <TableCell>{doc.change_kind}</TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">
                                      {toReviewStatusLabel(doc._status)}
                                    </Typography>
                                    {doc._message ? (
                                      <Typography
                                        variant="caption"
                                        color={
                                          doc._status === "error"
                                            ? "error"
                                            : "text.secondary"
                                        }
                                      >
                                        {doc._message}
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        handlePreviewDocumentDiff(doc)
                                      }
                                    >
                                      Raw diff
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        handlePreviewDocumentTracked(doc)
                                      }
                                    >
                                      Tracked preview
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() =>
                                        handleSaveImportedDocument(doc)
                                      }
                                      disabled={
                                        doc._status === "saved" ||
                                        doc._status === "saving"
                                      }
                                    >
                                      Save
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Apply summary
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2">
                    Changed text/code files, binary assets, and document updates
                    are separated in this review so you can verify the correct
                    asset type before saving.
                  </Typography>
                  <Typography variant="body2">
                    Use the grouped review above to spot which modules have
                    multiple changed items, then save only the items you are
                    ready to apply.
                  </Typography>
                  {hasSkippedFiles || hasSkippedDocuments ? (
                    <Typography variant="body2" color="warning.main">
                      There are skipped items in this package. Review them below
                      before treating the import as complete.
                    </Typography>
                  ) : null}
                  {createableSkippedFiles.length ? (
                    <Typography variant="body2" color="info.main">
                      Save All will create {createableSkippedFiles.length} safe
                      new file(s) before saving existing changed items.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="subtitle2">
                      Post-Import Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      After saving changed files, run the existing DEV IDE
                      project commands from this review modal so browser agents
                      can verify the change without SSH. Use standalone actions
                      or the sticky Save All and Verify button for a chained run.
                    </Typography>
                  </Box>

                  {saveAndVerifyStatus.status !== "Not run" ? (
                    <Alert
                      data-testid="import-zip-verify-status"
                      severity={
                        saveAndVerifyStatus.status === "Failed"
                          ? "error"
                          : saveAndVerifyStatus.status === "Passed"
                            ? "success"
                            : "info"
                      }
                    >
                      {saveAndVerifyStatus.message}
                    </Alert>
                  ) : null}

                  {!importSummary.savedCount ? (
                    <Alert severity="info">
                      Save changed files before running standalone post-import
                      verification actions, or use Save All and Verify to run the
                      chained workflow.
                    </Alert>
                  ) : null}

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(POST_IMPORT_ACTIONS).map(([key, action]) => {
                      const actionState = postImportActions[key] || {};
                      const chipProps = renderVerificationChipProps(
                        actionState.status || "Not run",
                      );
                      return (
                        <Stack
                          key={key}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRunPostImportAction(key)}
                            disabled={
                              !importSummary.savedCount ||
                              actionState.status === "Running" ||
                              saveAndVerifyLoading
                            }
                          >
                            {actionState.status === "Running"
                              ? "Running..."
                              : action.label}
                          </Button>
                          <Chip
                            size="small"
                            label={actionState.status || "Not run"}
                            color={chipProps.color}
                            variant={chipProps.variant}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>

                  {Object.entries(POST_IMPORT_ACTIONS).map(([key, action]) => {
                    const actionState = postImportActions[key] || {};
                    const hasOutput = Boolean(
                      actionState.output || actionState.error,
                    );
                    if (!hasOutput) return null;

                    return (
                      <Paper key={`${key}-output`} variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {action.label} output
                        </Typography>
                        {actionState.error ? (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                          >
                            {actionState.error}
                          </Typography>
                        ) : null}
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 0.75,
                            maxHeight: 180,
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: 12,
                          }}
                        >
                          {actionState.output || "No output returned."}
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>

              {hasSkippedDocuments ? (
                <>
                  <Typography variant="subtitle2">
                    Skipped documents ({skippedDocuments.length})
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    {(skippedDocuments || []).slice(0, 20).map((s, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        {s.path}: {s.reason}
                      </Typography>
                    ))}
                  </Paper>
                </>
              ) : null}

              {skipped && skipped.length > 0 ? (
                <>
                  <Typography variant="subtitle2">
                    Skipped files ({skipped.length})
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    {createableSkippedFiles.length ? (
                      <Box sx={{ mb: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            createMissingImportFiles(createableSkippedFiles, {
                              successPrefix: "Created",
                            })
                          }
                          disabled={
                            loading ||
                            createableSkippedFiles.some(
                              (file) => !!creatingMap[(file.path || "").toString()],
                            )
                          }
                        >
                          Create all safe new files
                        </Button>
                      </Box>
                    ) : null}
                    {(skipped || []).slice(0, 20).map((s, idx) => {
                      const canCreate = !!s.can_create;
                      const isCreating =
                        !!creatingMap[(s.path || "").toString()];

                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={`${s.path}: ${s.reason}`}
                            >
                              {s.path}: {s.reason}
                            </Typography>
                            <BinaryAssetDetails file={s} />
                          </Box>
                          {canCreate ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleCreateMissingFile(s)}
                              disabled={loading || isCreating}
                            >
                              {isCreating ? "Creating..." : "Create file"}
                            </Button>
                          ) : null}
                        </Box>
                      );
                    })}
                    {skipped.length > 20 ? (
                      <Typography variant="caption" color="text.secondary">
                        ...and {skipped.length - 20} more
                      </Typography>
                    ) : null}
                  </Paper>
                </>
              ) : null}
            </>
          ) : null}
        </Stack>

        {/* Diff Preview */}
        <Dialog
          open={diffOpen}
          onClose={() => {
            setDiffOpen(false);
            setDiffCopyStatus(""); // NEW
            setAllDiffsLoading(false);
            setAllDiffsStatus("");
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Diff: {diffTitle}</DialogTitle>
          <DialogContent>
            {diffLoading ? <LinearProgress /> : null}
            {diffErr ? <Alert severity="error">{diffErr}</Alert> : null}
            {diffCopyStatus ? (
              <Alert
                severity={
                  diffCopyStatus === "Copy failed"
                    ? "error"
                    : diffCopyStatus === "Manual copy mode opened"
                      ? "info"
                      : "success"
                }
                sx={{ mt: 1 }}
              >
                {diffCopyStatus}
              </Alert>
            ) : null}
            <Paper
              variant="outlined"
              sx={{ p: 1, mt: 1, maxHeight: "60vh", overflow: "auto" }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {diffText || "(no diff)"}
              </pre>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCopyDiff}
              variant="outlined"
              disabled={diffLoading || (!diffTitle && !diffText)}
            >
              Copy
            </Button>
            <Button onClick={() => setDiffOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={manualCopyOpen}
          onClose={() => setManualCopyOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Manual copy: {manualCopyTitle}</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              Clipboard write is blocked or unreliable in this device/browser
              context. We attempt to select the diff text automatically. On
              iPhone, tap and hold in the text box, then tap Copy.
            </Alert>
            <TextField
              inputRef={manualCopyRef}
              value={manualCopyText}
              onChange={() => {}}
              fullWidth
              multiline
              minRows={16}
              maxRows={24}
              margin="normal"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", alignItems: "flex-start" },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (!manualCopyRef.current) return;
                manualCopyRef.current.focus();
                manualCopyRef.current.select();
                manualCopyRef.current.setSelectionRange(
                  0,
                  manualCopyText.length,
                );
              }}
              variant="outlined"
            >
              Select all
            </Button>
            <Button onClick={() => setManualCopyOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={documentReviewOpen}
          onClose={() => setDocumentReviewOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Document review:{" "}
            {documentReviewDoc?.path ||
              documentReviewDoc?.title ||
              "Imported document"}
          </DialogTitle>
          <DialogContent dividers>
            {documentReviewLoading ? <LinearProgress /> : null}
            {documentReviewErr ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {documentReviewErr}
              </Alert>
            ) : null}
            {documentReviewDoc ? (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`Title: ${documentReviewDoc.title || "Untitled"}`}
                  />
                  <Chip
                    size="small"
                    label={`Modules: ${(documentReviewDoc.module_names || []).join(", ") || "None"}`}
                  />
                  <Chip
                    size="small"
                    color="success"
                    label={`+${documentReviewDiff?.added_lines || 0} lines`}
                  />
                  <Chip
                    size="small"
                    color="error"
                    label={`-${documentReviewDiff?.removed_lines || 0} lines`}
                  />
                </Stack>

                <Tabs
                  value={documentReviewTab}
                  onChange={(_, value) => setDocumentReviewTab(value)}
                >
                  <Tab value="tracked" label="Tracked changes" />
                  <Tab value="preview" label="Updated markdown preview" />
                  <Tab value="raw" label="Raw diff" />
                </Tabs>

                <Paper
                  variant="outlined"
                  sx={{ p: 2, maxHeight: "60vh", overflow: "auto" }}
                >
                  {documentReviewTab === "tracked" ? (
                    <TrackedDocumentDiff diff={documentReviewDiff} />
                  ) : documentReviewTab === "preview" ? (
                    <MarkdownPreview
                      content={documentReviewDoc.updated_content || ""}
                    />
                  ) : (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {documentReviewDiff?.unified_diff || "(no diff)"}
                    </Box>
                  )}
                </Paper>
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDocumentReviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <ImportZipModulariseReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onRunReview={handleRunReview}
          loading={reviewLoading}
          error={reviewErr}
          review={reviewResult}
          targetFilePath={reviewTargetPath}
        />
      </DialogContent>

      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
          {readyChangedImportCount} changed/new item(s) ready to save from this import review
          {saveAutomationBlockedByRiskGate ? " — Save All held by risk gate" : ""}
        </Typography>
        <Button
          data-testid="import-zip-save-and-verify"
          variant="contained"
          onClick={handleSaveAllAndVerify}
          disabled={
            loading ||
            saveAllLoading ||
            saveAndVerifyLoading ||
            (!readyChangedImportCount && !importSummary.savedCount) ||
            saveAutomationBlockedByRiskGate
          }
        >
          {saveAndVerifyLoading ? "Saving and verifying..." : "Save All and Verify"}
        </Button>
        <Button
          data-testid="import-zip-save-changed"
          variant="outlined"
          onClick={handleSaveAllChangedFiles}
          disabled={
            !readyChangedImportCount ||
            loading ||
            saveAllLoading ||
            saveAndVerifyLoading ||
            saveAutomationBlockedByRiskGate
          }
        >
          {saveAllLoading ? "Saving..." : "Save All Changed Files"}
        </Button>
        <Button data-testid="import-zip-close" onClick={handleClose} disabled={saveAndVerifyLoading}>Close Review</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportZipDialog;
